/**
 * Social listening (RND-2900): trackers collect public posts through a
 * provider (ScrapeCreators) into the Knowledge Hub. A sync writes its new posts as one
 * digest document through the canonical Hub ingest; each post is one `post`
 * span in that digest, so citations resolve to a single post.
 */

import type { AssistantSocialFocus } from "./assistant-request.generated";
import { ProposeSocialTrackerInput } from "./assistant-tools.generated";

export const SOCIAL_PLATFORMS = [
  "reddit",
  "youtube",
  "tiktok",
  "instagram",
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/**
 * Data providers a channel collects through. ScrapeCreators is the only
 * runtime path; `apify` stays compiled and dormant (D5): no runtime route,
 * no fallback. Kept so stored rows and the provider seam still type.
 */
export const SOCIAL_PROVIDERS = ["apify", "scrapecreators"] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

export const SOCIAL_TRACKER_STATUSES = [
  "queued",
  "collecting",
  "indexing",
  "ready",
  "partial",
  "failed",
  "rate_limited",
  "over_budget",
  "paused",
] as const;
/** Persisted tracker status. `stale` is derived, never stored. */
export type SocialTrackerStatus = (typeof SOCIAL_TRACKER_STATUSES)[number];

export const SOCIAL_SYNC_RUN_STATUSES = [
  "queued",
  "running",
  "indexing",
  "succeeded",
  "partial",
  "failed",
  "rate_limited",
  "over_budget",
  /** Closed without starting (no provider call): see `failureReason` (`out_of_credits`, `paused_by_ops`). */
  "skipped",
] as const;
export type SocialSyncRunStatus = (typeof SOCIAL_SYNC_RUN_STATUSES)[number];

/**
/**
 * Why a run failed or closed short, when the reason is typed:
 * - `queue_lost`: the queue lost the run's job (for example the backend
 *   restarted) and a re-queue did not recover it;
 * - `judge_not_configured`: the post judge (Jev) has no key, so the collect
 *   was refused before any provider call (nothing could sort the posts);
 * - `provider_not_configured`: the data provider (ScrapeCreators) has no
 *   key, so the collect was refused before any call;
 * - `stopped_early`: the provider stopped before the end (for example the
 *   provider account is out of credits, a 402), so the run closes `partial`
 *   with some posts missing, or `failed` when it had found none. Never the
 *   wallet's `out_of_credits`;
 * - `provider_blocked`: the provider answered that it could not search,
 *   before any post was seen (the run fails);
 * - `replaced`: a newer request found the run lost and replaced it
 *   (`failOpenRuns` writes it);
 * - `budget`: the run stopped on the org's budget (status `partial`);
 * - `empty_after_filters`: the provider returned posts but the filters
 *   (window, language, tombstones, relevance) dropped all of them;
 * - `out_of_credits`: the wallet could not hold the run's credits, so it
 *   never started (status `skipped`, nothing charged);
 * - `paused_by_ops`: the kill switch stopped it (hold released, nothing
 *   charged).
 * - `paused`: the client paused the channel before the run finished. Not a
 *   failure: the run closes `partial` with what it found, and the channel's
 *   failure count is untouched.
 * - `judge_unavailable`: set after the close, once the final judge ask failed
 *   too. Nothing waits to be sorted any more, and the run stays `partial` so
 *   the outage shows; a later round that sorts the posts clears it.
 */
export const SOCIAL_SYNC_RUN_FAILURE_REASONS = [
  "queue_lost",
  "judge_not_configured",
  "provider_not_configured",
  "stopped_early",
  "provider_blocked",
  "replaced",
  "budget",
  "empty_after_filters",
  "out_of_credits",
  "paused_by_ops",
  "paused",
  "judge_unavailable",
] as const;
export type SocialSyncRunFailureReason =
  (typeof SOCIAL_SYNC_RUN_FAILURE_REASONS)[number];

/**
 * Why a channel shows `partial` or `failed`, derived by the server from its
 * latest closed run's typed fields (never from its error text):
 * - `stopped_early`: partial, the provider stopped early, posts may be missing;
 *   or failed, the provider stopped (out of credits) before any post;
 * - `not_sorted`: partial, every post was collected and some still wait for
 *   the judge, which retries on its own; or the close's final ask failed too
 *   and no retry is left (`sortingStopped` on the channel);
 * - `judge_not_configured`, `provider_not_configured`: failed, the update was
 *   refused before collecting (the judge or the provider has no key).
 * Null for every other status, and for a failure without a typed reason.
 */
export const SOCIAL_CHANNEL_STATUS_REASONS = [
  "stopped_early",
  "not_sorted",
  "judge_not_configured",
  "provider_not_configured",
] as const;
export type SocialChannelStatusReason =
  (typeof SOCIAL_CHANNEL_STATUS_REASONS)[number];

/** Heading the digest writer emits per post; segmentation splits on it. */
export const SOCIAL_DIGEST_POST_HEADING_PREFIX = "## [post:";

/** v1 fixed settings (PRD section 8). */
/** Orders of a tracker's post list: newest first, or most comments or likes first. */
export const SOCIAL_POSTS_SORTS = ["newest", "comments", "likes"] as const;
export type SocialPostsSort = (typeof SOCIAL_POSTS_SORTS)[number];

export const SOCIAL_SYNC_CADENCE_HOURS = 24;
/**
 * Server schedule (SOCIAL_LISTENING_SCHEDULE): `daily` syncs every tracker on
 * the cadence; `manual` (testing environments) syncs only on demand: the
 * first import, Sync now and Retry.
 */
export const SOCIAL_SYNC_SCHEDULES = ["daily", "manual"] as const;
export type SocialSyncSchedule = (typeof SOCIAL_SYNC_SCHEDULES)[number];
/** A tracker is stale when its last success is older than 2x the cadence. */
export const SOCIAL_STALE_AFTER_HOURS = SOCIAL_SYNC_CADENCE_HOURS * 2;
export const SOCIAL_DEFAULT_RETENTION_DAYS = 90;

/**
 * How far back a tracker's first run starts, in days. Later runs continue
 * from the newest post seen.
 */
export const SOCIAL_DEPTH_DAYS = [7, 30, 90, 365] as const;
export type SocialDepthDays = (typeof SOCIAL_DEPTH_DAYS)[number];

/** How often a tracker updates; `on_demand` updates only when the client asks. */
export const SOCIAL_TRACKER_CADENCES = [
  "daily",
  "weekly",
  "on_demand",
] as const;
export type SocialTrackerCadence = (typeof SOCIAL_TRACKER_CADENCES)[number];

/** Hours between scheduled updates; `on_demand` is never scheduled. */
export const SOCIAL_CADENCE_HOURS: Record<
  Exclude<SocialTrackerCadence, "on_demand">,
  number
> = {
  daily: 24,
  weekly: 24 * 7,
};

/**
 * Relevant posts a week per channel (owner Q1): the rate every run size
 * derives from (the run plan's update target, `socialChannelRunPlan`).
 * Stored as `run_post_limit`.
 */
export const SOCIAL_RUN_POST_LIMITS = [50, 100, 200, 300, 500] as const;
export type SocialRunPostLimit = (typeof SOCIAL_RUN_POST_LIMITS)[number];

/**
 * Relevant posts per channel a first collection keeps at most (owner,
 * 2026-11-01): the target of every first import (a new Topic, a brand,
 * search or channel added later), in place of the size derived from the
 * weekly rate. Updates keep the weekly sizing. Stored as
 * `first_collection_post_limit` on the Topic and its channels; a new limit
 * applies to later first imports only.
 */
export const SOCIAL_FIRST_COLLECTION_POST_LIMITS = [100, 250, 500, 1000, 1500] as const;
export type SocialFirstCollectionPostLimit =
  (typeof SOCIAL_FIRST_COLLECTION_POST_LIMITS)[number];

/**
 * Defaults for new trackers. Trackers created before these keep their look-back
 * and cadence; their old per-run cap became a weekly rate in migration 0257
 * (daily x 7, weekly and on demand as is, rounded up to the next
 * SOCIAL_RUN_POST_LIMITS option, at most 300); a tracker on 500 then went to
 * 300 and picks 500 again in its settings (owner, 2026-11-01: 500 is back on
 * top for volume; the 1,500 run ceiling still bounds a run).
 */
export const SOCIAL_DEFAULT_DEPTH_DAYS: SocialDepthDays = 30;
export const SOCIAL_DEFAULT_CADENCE: SocialTrackerCadence = "weekly";
export const SOCIAL_DEFAULT_RUN_POST_LIMIT: SocialRunPostLimit = 50;
export const SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT: SocialFirstCollectionPostLimit = 250;
export const SOCIAL_ALERT_AFTER_CONSECUTIVE_FAILURES = 2;

/**
 * Why a channel is paused (`status` stays `paused`): `user` (Pause),
 * `out_of_credits` (the wallet could not hold its next update; retried at the
 * channel's own schedule for SOCIAL_CREDIT_PAUSE_RETRY_DAYS, then Resume),
 * `ops` (kill switch, restarts on its own).
 */
export const SOCIAL_TRACKER_PAUSE_REASONS = ["user", "out_of_credits", "ops"] as const;
export type SocialTrackerPauseReason = (typeof SOCIAL_TRACKER_PAUSE_REASONS)[number];

export interface SocialTrackerInput {
  name: string;
  platform: SocialPlatform;
  /** One or more search terms; matched by the provider, never by us. */
  keywords: Array<string>;
  /** Optional accounts (subreddits / channels / profiles) to collect from. */
  handles?: Array<string>;
  /** BCP-47 language, e.g. "en", "fr". */
  language: string;
  /** Market label, e.g. "France". */
  market: string;
  /**
   * ISO 3166-1 alpha-2 code of the `COUNTRIES` entry the user picked for
   * `market`; null when the market is not a list entry (no single country).
   * The provider `region` comes from it, never from the name. Absent: the
   * server derives it from `market` with the country map (matrix row 7).
   */
  marketCode?: string | null;
  /** Library labels put on every digest document the tracker writes. */
  labels?: Array<SocialTrackerLabel>;
  /** What counts as relevant; every new post is judged against it. */
  brief?: SocialTrackerBrief;
  /** What each keyword means; a note for a keyword not in `keywords` is dropped. */
  keywordNotes?: Array<SocialKeywordNote>;
  /** Tags put on relevant posts; send an existing topic's id to keep it. */
  topics?: Array<SocialTopicInput>;
  /** First-run range; defaults to SOCIAL_DEFAULT_DEPTH_DAYS. */
  depthDays?: SocialDepthDays;
  /** Defaults to SOCIAL_DEFAULT_CADENCE. */
  cadence?: SocialTrackerCadence;
  /** Posts per run; defaults to SOCIAL_DEFAULT_RUN_POST_LIMIT. */
  runPostLimit?: SocialRunPostLimit;
  /** First collection posts limit; defaults to SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT. */
  firstCollectionPostLimit?: SocialFirstCollectionPostLimit;
  /**
   * Every language a post may be in (BCP-47); a post in any of them is kept.
   * Defaults to `[language]`. Absent on clients from before Topics.
   */
  languages?: Array<string>;
}

/**
 * The tracker's relevance brief. A brand tracker keeps posts about the brand
 * only; `include` and `exclude` say what that covers in the client's words.
 */
export interface SocialTrackerBrief {
  include: string;
  exclude: string;
}

export interface SocialKeywordNote {
  keyword: string;
  description: string;
}

/** A per-tracker tag on posts. The id is the server's and survives a rename. */
export interface SocialTopic {
  id: string;
  name: string;
  description: string;
}

/** A topic as a client sends it: without an id when it is new. */
export interface SocialTopicInput {
  id?: string;
  name: string;
  description: string;
}

/**
 * True when a keyword is meant as a hashtag (the user typed a leading `#`).
 */
export function isHashtagTerm(term: string): boolean {
  return term.trim().startsWith("#");
}

// RegExp constructor keeps the `u` flag valid under the frontend's es5 target.
const NON_HASHTAG_CHARS = new RegExp("[^\\p{L}\\p{M}\\p{N}_]", "gu");

/**
 * The one hashtag shape every channel accepts: NFC-normalized, leading `#`
 * stripped, and every character that is not a Unicode letter, mark, digit or
 * `_` removed (whitespace, apostrophes, hyphens, dots, emoji). Accents and
 * case are kept. Returns `''` when nothing remains, so callers skip the term.
 */
export function toHashtag(term: string): string {
  return term
    .normalize("NFC")
    .trim()
    .replace(/^#+/, "")
    .replace(NON_HASHTAG_CHARS, "");
}

/** Keywords a Topic or tracker searches for, at most. */
export const SOCIAL_MAX_KEYWORDS = 20;
export const SOCIAL_MAX_TOPICS = 12;
export const SOCIAL_BRIEF_MAX_LENGTH = 1000;
export const SOCIAL_NOTE_MAX_LENGTH = 300;
export const SOCIAL_TOPIC_NAME_MAX_LENGTH = 60;
/** Off-topic posts a run keeps as `{url, reason}` so the client can check the filter. */
export const SOCIAL_OFF_TOPIC_SAMPLES_PER_RUN = 10;

/** Facet kinds a client can label a tracker's Library documents with. */
export const SOCIAL_TRACKER_LABEL_KINDS = [
  "brand",
  "market",
  "category",
] as const;
export type SocialTrackerLabelKind =
  (typeof SOCIAL_TRACKER_LABEL_KINDS)[number];

export interface SocialTrackerLabel {
  kind: SocialTrackerLabelKind;
  value: string;
}

/**
 * A tracker suggested from the org's context, by the Ask agent
 * (`propose_social_tracker`) or by Library's "Suggest with AI". Nothing is
 * created until the client confirms it.
 *
 * Derived from the generated `ProposeSocialTrackerInput` (the python registry owns the
 * shape and its bounds); only `createdTrackerToken` is added here. Fields the generated
 * input leaves optional are required here because the server fills them; `brief`,
 * `keywordNotes` and `topics` stay optional for proposals saved before them.
 */
export interface SocialTrackerProposal
  extends
    Required<
      Pick<
        ProposeSocialTrackerInput,
        | "name"
        | "platform"
        | "keywords"
        | "language"
        | "market"
        | "labels"
        | "rationale"
      >
    >,
    Partial<
      Pick<
        ProposeSocialTrackerInput,
        "brief" | "keywordNotes" | "topics"
      >
    > {
  /**
   * Typed sources the assistant proposed (collection v2). Declared here, not
   * picked from the generated tool input, so this contract holds whichever
   * tool schema version is generated.
   */
  hashtags?: Array<string>;
  communities?: Array<string>;
  accounts?: Array<string>;
  /**
   * `keywords` and `handles` are the old-wire mirror of the typed sources for
   * one release (P9, `socialLegacyChannelColumns`): the searches, then on
   * TikTok and Instagram each hashtag as `#tag`; on Reddit the communities,
   * elsewhere the accounts. The card creates the tracker from them, and the
   * one transform (`socialTypedChannelInputs`) types them again.
   */
  handles: Array<string>;
  /** Set once the client created a tracker from this proposal. */
  createdTrackerToken: string | null;
}

export interface SocialTrackerSuggestionRequest {
  /** What the client wants to learn; optional. */
  goal?: string;
  /** Explicit search terms and meanings from the Topics step; returned unchanged. */
  seed?: {
    keywords: Array<string>;
    keywordNotes: Array<SocialKeywordNote>;
  };
  /** The platform the client already picked; the model picks one when absent. */
  platform?: SocialPlatform;
}

export interface SocialTrackerItem {
  token: string;
  name: string;
  provider: SocialProvider;
  platform: SocialPlatform;
  keywords: Array<string>;
  handles: Array<string>;
  language: string;
  market: string;
  /** The market's country code; null when it has none. */
  marketCode: string | null;
  labels: Array<SocialTrackerLabel>;
  brief: SocialTrackerBrief;
  keywordNotes: Array<SocialKeywordNote>;
  topics: Array<SocialTopic>;
  depthDays: SocialDepthDays;
  cadence: SocialTrackerCadence;
  runPostLimit: SocialRunPostLimit;
  /** Most relevant posts per channel a charged first collection keeps; those past it wait for the run's unlock (SOCIAL_FIRST_COLLECTION_POST_LIMITS). */
  firstCollectionPostLimit: SocialFirstCollectionPostLimit;
  status: SocialTrackerStatus;
  /** Why the status is `partial` or `failed`, when typed; absent on older servers. */
  statusReason?: SocialChannelStatusReason | null;
  /** With `not_sorted`: the final judge ask failed, nothing retries until a new update. */
  sortingStopped?: boolean;
  /** Derived: last success older than 2x the cadence; never for `on_demand`. */
  stale: boolean;
  lastError: string | null;
  lastSyncedAt: string | null;
  lastAttemptAt?: string | null;
  newestPostAt: string | null;
  postsLast7Days: number;
  /** Collected posts published in the past 30 days; absent on older servers. */
  postsLast30Days?: number;
  totalPosts: number;
  consecutiveFailures: number;
  /** New posts in the latest finished run; 0 renders "no new posts today". */
  lastRunNewPosts?: number | null;
  /** Knowledge collection that scopes "Ask about this tracker". */
  collectionToken: string | null;
  retentionDays: number;
  createdAt: string;
  /**
   * The Topic (internal `universe`) this tracker is a channel of. A channel's
   * config is edited through its Topic only. Absent on servers before Topics.
   */
  universeToken?: string | null;
  /** Every language kept (BCP-47); absent on servers before Topics. */
  languages?: Array<string>;
  /** Why the channel is paused; null when not paused. Absent on servers before the wallet. */
  pauseReason?: SocialTrackerPauseReason | null;
  /** End of the out-of-credits auto-resume window; null unless paused `out_of_credits`. */
  autoResumeEndsAt?: string | null;
}

export interface SocialTrackersResponse {
  /**
   * False when SOCIAL_LISTENING_ENABLED is off, or (member routes) the org flag
   * `socialListening` is off: the UI hides the tab.
   */
  enabled: boolean;
  /** OWNER or ADMIN: may add, edit, pause, delete and remove posts. */
  canManage: boolean;
  /** The server's sync schedule; `manual` has no daily sync. */
  schedule: SocialSyncSchedule;
  /**
   * True when Instagram collects through keyword search (ScrapeCreators):
   * Instagram keywords may hold spaces. False keeps the one-word hashtag rule.
   */
  instagramKeywordSearch?: boolean;
  trackers: Array<SocialTrackerItem>;
  /**
   * Back-office reads only: the org's `socialListening` flag. The operator
   * still manages the org's trackers while it is off.
   */
  orgFlagEnabled?: boolean;
}

export interface SocialTrackerUpdate {
  name?: string;
  keywords?: Array<string>;
  handles?: Array<string>;
  language?: string;
  market?: string;
  labels?: Array<SocialTrackerLabel>;
  brief?: SocialTrackerBrief;
  keywordNotes?: Array<SocialKeywordNote>;
  topics?: Array<SocialTopicInput>;
  /** Applies to the next first run only; later runs continue from the newest post. */
  depthDays?: SocialDepthDays;
  cadence?: SocialTrackerCadence;
  runPostLimit?: SocialRunPostLimit;
  /** Applies to later first collections only; defaults to SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT (or the saved value). */
  firstCollectionPostLimit?: SocialFirstCollectionPostLimit;
}

export interface SocialSyncRunsResponse {
  runs: Array<SocialSyncRunItem>;
}

/**
 * HTTP contract (all org-scoped, JWT cookie):
 *   GET    /social-listening/trackers                      -> SocialTrackersResponse
 *   POST   /social-listening/trackers          (manage)    SocialTrackerInput -> SocialTrackerItem
 *   PATCH  /social-listening/trackers/:token   (manage)    SocialTrackerUpdate -> SocialTrackerItem
 *   POST   /social-listening/suggestions       (manage)    SocialTrackerSuggestionRequest -> SocialTrackerProposal
 *   POST   /social-listening/trackers/:token/sync   (manage) -> SocialTrackerItem   (run now / retry)
 *   POST   /social-listening/trackers/:token/pause  (manage) -> SocialTrackerItem
 *   POST   /social-listening/trackers/:token/resume (manage) -> SocialTrackerItem
 *   DELETE /social-listening/trackers/:token   (manage)    hard delete -> 204
 *   GET    /social-listening/trackers/:token/posts?cursor=&limit= -> SocialPostsResponse
 *   GET    /social-listening/trackers/:token/runs          -> SocialSyncRunsResponse
 *   DELETE /social-listening/posts/:token      (manage)    remove one post -> 204
 *   DELETE /social-listening/trackers/:token/authors/:handle (manage) remove an author's posts -> 204
 *   GET    /knowledge/documents?source=docs|social  (default docs: digests excluded)
 *   Ask bodies accept `sourceKinds?: Array<KnowledgeSourceKind>` and `socialWindow?: SocialWindow`.
 */

export interface SocialPostEngagement {
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  views?: number | null;
}

export interface SocialPostItem {
  token: string;
  platform: SocialPlatform;
  url: string;
  authorHandle: string | null;
  publishedAt: string | null;
  collectedAt: string;
  language: string | null;
  text: string;
  engagement: SocialPostEngagement;
  /** The platform's official embed of the post, loaded on demand; null when the platform has none. */
  embedUrl: string | null;
  media: SocialPostMedia;
  /** Ids of the tracker's topics the post was tagged with. */
  topicIds: Array<string>;
  /**
   * The seven fields below come with Topics (Jev tagging). They are absent on
   * servers before Topics; `SocialTopicPostItem` makes them required.
   */
  brands?: Array<SocialPostBrandTag>;
  /** Null when the post was judged before Jev (no sentiment then). */
  sentiment?: SocialSentiment | null;
  providerMeta?: SocialProviderMeta;
  /** Derived from `providerMeta.followers`; null when the platform gave none. */
  followerTier?: SocialFollowerTier | null;
  /** Stored images (cover, video frames, photos), served by the image proxy. */
  images?: Array<SocialPostImage>;
  /** Null while unjudged; `openai` for posts judged before Jev. */
  judgedBy?: SocialJudge | null;
  /** Hidden from its Topic by a manager; listings exclude hidden posts. */
  hidden?: boolean;
  /**
   * The creator's stored profile picture, a relative image proxy path (see
   * `socialAuthorAvatarPath`); null when none is stored (initials then).
   */
  authorAvatarPath?: string | null;
}

/**
 * Provider media links as collected. Instagram and TikTok sign them, so a
 * thumbnail stops loading a few days after collection; the embed does not.
 */
export interface SocialPostMedia {
  thumbnailUrl: string | null;
  imageCount: number;
  hasVideo: boolean;
}

export interface SocialPostsResponse {
  posts: Array<SocialPostItem>;
  nextCursor: string | null;
}

export interface SocialSyncRunItem {
  token: string;
  status: SocialSyncRunStatus;
  itemsFetched: number;
  itemsNew: number;
  /**
   * Posts the provider charged for (its per-result events); every one counts,
   * shown or not. Null on runs from before it was recorded.
   */
  postsFetched: number | null;
  /** Posts the provider returned from before the tracker's range, trimmed by us. */
  itemsOutOfWindow: number;
  /** Back-office test limit this run was capped at (local only). */
  postLimit: number | null;
  /**
   * Range in days this run asked for when it started from the tracker's range
   * (no earlier post collected); null when it continued from the newest post.
   */
  depthDays: number | null;
  /** New posts judged not about the tracker's subject and deleted. */
  itemsOffTopic: number;
  costUsd: number | null;
  error: string | null;
  /** Typed failure reason; null when untyped. Absent on servers before it. */
  failureReason?: SocialSyncRunFailureReason | null;
  startedAt: string | null;
  finishedAt: string | null;
  /**
   * The four fields below come with Topics; absent on servers before them.
   * Posts of this run kept and listed (relevant, in range).
   */
  postsShown?: number;
  /** Posts of this run Jev judged not relevant to the Topic. */
  postsNotRelevant?: number;
  /** Posts still waiting for a verdict (Jev failed); retried by the next judge job. */
  itemsUnjudged?: number;
  /** Where the run is in collect, media, judge, digest; `done` once closed. */
  phase?: SocialRunPhase;
  /**
   * Of `postsFetched`: relevant posts dropped because the run reached its
   * limit (the rounds' `over_target`, less those in `heldOffer`), plus, on runs
   * before rounds, in-range posts the provider returned beyond the run's cap.
   * Absent on servers before it.
   */
  postsOverLimit?: number;
  /**
   * Relevant posts held past the run's limit that accepting adds, at the run's
   * rate per post. Absent when there is no offer: none held, the run not
   * settled yet, already accepted, or past the Topic's retention.
   */
  heldOffer?: SocialRunHeldOffer;
  /**
   * The fields below come with collection v2 (rounds); absent on servers
   * and runs before it. Why a successful run stopped collecting; null on
   * every other close (an abnormal end keeps its status and failure reason,
   * see SOCIAL_RUN_ENDS) and while the run is open.
   */
  stopReason?: SocialRunStopReason | null;
  /** The run's plan groups with what each accepted. */
  groups?: Array<SocialRunGroupItem>;
  /** Accepted posts of the run by their typed source. */
  sourceSplit?: SocialPostSourceSplit;
  /** Candidate videos judged without a transcript because the transcript credits ran out. */
  transcriptsSkipped?: number;
  /** Comments read for the run's accepted posts. */
  commentsRead?: SocialCommentsRead;
  /**
   * Of the posts added (`itemsNew`, frozen at close): those the run no longer
   * holds as listed, because they were hidden or removed. Absent when none.
   */
  postsHiddenOrRemoved?: number;
  /**
   * Wallet fields (credits as decimals, up to 3 places: millicredits / 1000).
   * Absent on servers before the wallet; null while not applicable (charging
   * off, pre-cutover, admin syncs). Relevant (accepted) posts the run added,
   * frozen at close: the billed unit (D3). Filtered posts are never counted.
   */
  postsKept?: number | null;
  /** Credits set aside for the run while it is open (and what was set aside once settled). */
  creditsHeld?: number | null;
  /** The run's charge once settled; 0 for failed, killed or skipped runs. */
  creditsCharged?: number | null;
  /** Held minus charged once settled or released. */
  creditsReturned?: number | null;
  /** Back office only: which term set the charge. Never shown to customers. */
  chargeBasis?: SocialChargeBasis | null;
  billingStatus?: SocialRunBillingStatus | null;
}

/** Default and bounds of the local back-office post limit per sync. */
export const SOCIAL_TEST_POST_LIMIT_DEFAULT = 50;
export const SOCIAL_TEST_POST_LIMIT_MIN = 1;

/** The local post limit per sync, as the back office reads and writes it. */
export interface SocialTestPostLimit {
  enabled: boolean;
  maxPosts: number;
  /** DIGITAL_TWINS_ENVIRONMENT of the backend serving it, e.g. `dev`. */
  environment: string;
}

export interface UpdateSocialTestPostLimitRequest {
  enabled: boolean;
  maxPosts: number;
}

/** Post metadata attached to a citation whose granularity is `post`. */
export interface SocialCitationPost {
  postToken: string;
  platform: SocialPlatform;
  url: string;
  authorHandle: string | null;
  publishedAt: string | null;
  trackerName: string | null;
  /** Name of the Topic the post belongs to; absent before Topics. */
  topicName?: string | null;
  /** The four fields below are absent on answers saved before they existed. */
  text?: string;
  engagement?: SocialPostEngagement;
  embedUrl?: string | null;
  /** Signed provider link; see `SocialPostMedia`. Never shown: it expires. */
  thumbnailUrl?: string | null;
  /** The post's first stored image (`SocialPostImage.imagePath`); null without one. */
  imagePath?: string | null;
}

/** `POST knowledge/social-posts/by-url`: the stored posts behind an answer's post links. */
export interface SocialPostsByUrlResponse {
  posts: Array<SocialCitationPost>;
}

/** The platform's official embed of a stored post; null when it has none. */
export function socialPostEmbedUrl(post: {
  platform: SocialPlatform;
  platformPostId: string;
  url: string;
}): string | null {
  const id = encodeURIComponent(post.platformPostId);
  switch (post.platform) {
    case "youtube":
      return `https://www.youtube-nocookie.com/embed/${id}`;
    case "tiktok":
      return `https://www.tiktok.com/player/v1/${id}`;
    case "instagram": {
      // Always the www host: the player check and the CSP frame-src allow only it.
      const permalink = new URL(post.url);
      return /(^|\.)instagram\.com$/.test(permalink.hostname)
        ? `https://www.instagram.com${permalink.pathname.replace(/\/$/, "")}/embed/`
        : null;
    }
    case "reddit": {
      const permalink = new URL(post.url);
      return /(^|\.)reddit\.com$/.test(permalink.hostname)
        ? `https://embed.reddit.com${permalink.pathname}?embed=true`
        : null;
    }
  }
}

/** Ask scope additions: restrict an ask to library documents or social digests. */
export const KNOWLEDGE_SOURCE_KINDS = ["docs", "social"] as const;
export type KnowledgeSourceKind = (typeof KNOWLEDGE_SOURCE_KINDS)[number];

export interface SocialWindow {
  /** ISO date-time, inclusive. */
  from: string;
  /** ISO date-time, inclusive. */
  to: string;
}

/** Longest `socialWindow` an ask accepts. */
export const SOCIAL_ASK_WINDOW_MAX_DAYS = 366;

/**
 * Source scope carried by an ask body and a thread scope. `sourceKinds`
 * `["social"]` keeps only social digests, `["docs"]` excludes them; both or
 * absent leaves the library unrestricted. `socialWindow` keeps the digests
 * holding at least one post published inside it, and implies `social`.
 *
 * `libraryCollectionTokens` (thread scope only, at most 5) scopes the
 * Assistant to Library collections: the server resolves their members as the
 * asking user, and an empty or invisible collection puts nothing in scope,
 * never the whole library.
 *
 * `socialFocus` (thread scope only) names the Topic, theme, tracker, filters
 * and posts on the screen the user pressed Ask on (plan 2026-11-01 D0, D1).
 * Context only, never a restriction. The thread keeps it across turns until a
 * scope names it again; `null` clears it.
 */
export interface KnowledgeAskSourceScope {
  sourceKinds?: Array<KnowledgeSourceKind>;
  socialWindow?: SocialWindow | null;
  libraryCollectionTokens?: Array<string>;
  socialFocus?: AssistantSocialFocus | null;
}

/* -------------------------------------------------------------------------- */
/* Topics (Phase 1b). Users see "Topic", "Theme", "Brand", "Channel"; the     */
/* internal names are universe, topic, brand and tracker. None of "universe", */
/* "tracker", "run", "window", "off topic" or "judged" reaches UI copy.       */
/* -------------------------------------------------------------------------- */

/** A brand tagged in a Topic's posts, from text, image or video. The id is the server's and survives a rename. */
export interface SocialBrand {
  id: string;
  name: string;
  /**
   * What the brand covers (its products, its line); may be empty. Other
   * spellings belong in `aliases`; brands saved before `aliases` may still
   * hold them here.
   */
  description: string;
  /**
   * What the channels that search brands (their `search.brands` rule) search
   * for this brand, 1 to SOCIAL_BRAND_SEARCHES_MAX, each as written; which
   * kinds a brand gets is the author's call (the Suggest prompt's brands
   * clause). Each has a stable id; the first keeps the brand's original source
   * id (`socialBrandSearchSourceId`). Written by Suggest, editable; code never
   * rewrites one.
   */
  searches: Array<SocialBrandSearch>;
  /**
   * The brand's own hashtags (written without `#`), at most
   * SOCIAL_BRAND_HASHTAGS_MAX, read on the channels that follow hashtags
   * (`search.hashtags: feed`) in the brand's group. Absent on brands saved
   * before D20.
   */
  hashtags?: Array<SocialChannelHashtag>;
  /**
   * Old-wire mirror of `searches[0].query` for one release (D20), written
   * only by the server's `normalizeBrands`; nothing new reads it.
   */
  searchTerm?: string;
  /** Searched on the brand channels; tagging reads every brand either way. */
  collect: boolean;
  /**
   * The three fields below come with collection v2; absent on brands saved
   * before it. The client's own brand or a competitor; both get the same
   * routes and effort (D14).
   */
  role?: SocialBrandRole;
  /**
   * Other spellings of the name (exact forms; a duplicate brand is one whose
   * normalised name or alias equals another's). They take over the spellings
   * role of `description`.
   */
  aliases?: Array<string>;
  /**
   * The brand's own account per platform, only on platforms whose provider
   * capability `ownPosts` is on (`config/social-providers.yaml`). Only a
   * confirmed account with a verified check gets account routes.
   */
  accounts?: SocialBrandAccounts;
  /**
   * Response only, never stored: the brand's logo, the stored profile picture
   * of its first confirmed account (instagram, tiktok, youtube), a relative
   * image proxy path (`socialAuthorAvatarPath`). Set by single-Topic reads;
   * null on the Topic list and when no picture is stored (the brand keeps its
   * color swatch). Optional for older clients and fixtures.
   */
  logoPath?: string | null;
}

/**
 * One search of a brand. `communitiesOnly` runs it only inside the channel's
 * communities (a `community_search` per community), never on the whole
 * platform and not on a channel with no communities; absent, it runs on every
 * channel that searches brands, and inside the communities too.
 */
export interface SocialBrandSearch extends SocialChannelSearch {
  communitiesOnly?: boolean;
}

/** A brand search as a client sends it. */
export interface SocialBrandSearchInput extends SocialChannelSearchInput {
  communitiesOnly?: boolean;
}

/** A brand as a client sends it: without an id when it is new. */
export interface SocialBrandInput {
  id?: string;
  name: string;
  description?: string;
  /**
   * 1 to SOCIAL_BRAND_SEARCHES_MAX searches, each at most
   * SOCIAL_SEARCH_TERM_MAX_LENGTH characters; `id` of a search the brand has,
   * absent for a new one. Required unless the old wire's `searchTerm` is sent.
   */
  searches?: Array<SocialBrandSearchInput>;
  /** At most SOCIAL_BRAND_HASHTAGS_MAX; `check` as on a channel hashtag. */
  hashtags?: Array<SocialChannelHashtagInput>;
  /**
   * The old wire (one release): one search, read only when `searches` is
   * absent, and kept as `searches[0]` (with its id when it is unchanged).
   */
  searchTerm?: string;
  /** Absent: collected (brand collection on by default). */
  collect?: boolean;
  role?: SocialBrandRole;
  aliases?: Array<string>;
  accounts?: SocialBrandAccounts;
}

/**
 * Most searches one brand carries (D20, raised from 3 on 2026-11-02 for the
 * market's own keywords): the same number for every brand, so more searches
 * split the same brand budget.
 */
export const SOCIAL_BRAND_SEARCHES_MAX = 10;
/** Most hashtags one brand carries (D20). */
export const SOCIAL_BRAND_HASHTAGS_MAX = 3;

export const SOCIAL_BRAND_ROLES = ["brand", "competitor"] as const;
export type SocialBrandRole = (typeof SOCIAL_BRAND_ROLES)[number];

/** One brand account on one platform. */
export interface SocialBrandAccount {
  /** As written, without a leading `@`; compared only through `normalizeSocialHandle`. */
  handle: string;
  /** The provider's user or channel id, resolved once at save; required once the check is `verified`. */
  providerId: string | null;
  check: SocialSourceCheck;
  /** Model-proposed accounts start false; accounts the client typed start true. */
  confirmed: boolean;
}

export type SocialBrandAccounts = Partial<
  Record<SocialPlatform, SocialBrandAccount>
>;

/** Longest search a channel takes, a brand search included. */
export const SOCIAL_SEARCH_TERM_MAX_LENGTH = 100;

/**
 * Why a channel does not take a search; checked on literal characters only.
 * `hashtag_is_a_source`: a search never starts with `#`, since a hashtag is
 * its own typed source; `quotes_stacked`: more quoted phrases than the
 * channel's `search.quotedPhrases` allows.
 */
export const SOCIAL_SEARCH_REFUSALS = [
  "empty",
  "too_long",
  "hashtag_is_a_source",
  "hashtag_with_space",
  "quotes_ignored",
  "quotes_stacked",
] as const;
export type SocialSearchRefusal = (typeof SOCIAL_SEARCH_REFUSALS)[number];

/**
 * Why a channel skips a brand's search: a refusal of the search itself, or
 * `channel_takes_name_only`, a channel whose search takes no extra words
 * (`search.extraWords: false`, config/social-providers.yaml) runs only the
 * brand's first search and skips the others (no credits).
 */
export const SOCIAL_BRAND_SEARCH_SKIPS = [
  ...SOCIAL_SEARCH_REFUSALS,
  "channel_takes_name_only",
] as const;
export type SocialBrandSearchSkipReason =
  (typeof SOCIAL_BRAND_SEARCH_SKIPS)[number];

/** A collected brand's search a channel does not take, and why; the search is never rewritten. */
export interface SocialBrandSearchSkip {
  brandId: string;
  /** The brand search's id (`SocialBrand.searches`). */
  searchId: string;
  query: string;
  refusal: SocialBrandSearchSkipReason;
  /** The refusal in one plain sentence. */
  reason: string;
}

/**
 * A brand's identity for duplicates: trimmed, inner spaces collapsed, Unicode
 * NFC and lower case. Two brands are one company when any of their names or
 * aliases are equal under it; nothing else is compared (no fuzzy matching).
 */
export function normalizeSocialBrandName(value: string): string {
  return value.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * The brand in `others` that `brand` duplicates: the first whose normalised
 * name or alias equals the normalised name or an alias of `brand`; null when
 * none does.
 */
export function socialBrandDuplicateOf(
  brand: Pick<SocialBrandInput, "name" | "aliases">,
  others: Array<Pick<SocialBrandInput, "name" | "aliases">>,
): string | null {
  const keys = new Set(
    [brand.name, ...(brand.aliases ?? [])].map(normalizeSocialBrandName),
  );
  const other = others.find((entry) =>
    [entry.name, ...(entry.aliases ?? [])].some((value) =>
      keys.has(normalizeSocialBrandName(value)),
    ),
  );
  return other ? other.name : null;
}

/** Why a brand was not kept; `duplicate`: its name or an alias is another brand's. */
export const SOCIAL_BRAND_REFUSALS = ["duplicate"] as const;
export type SocialBrandRefusal = (typeof SOCIAL_BRAND_REFUSALS)[number];

/** A suggested brand that was not kept, and why; stated, never folded silently. */
export interface SocialRefusedBrand {
  name: string;
  refusal: SocialBrandRefusal;
  /** The kept brand it duplicates. */
  duplicateOf: string;
  /** The refusal in one plain sentence. */
  reason: string;
}

export const SOCIAL_MAX_BRANDS = 12;
/** Most aliases one brand carries. */
export const SOCIAL_MAX_BRAND_ALIASES = 5;
export const SOCIAL_BRAND_NAME_MAX_LENGTH = 60;
export const SOCIAL_MAX_LANGUAGES = 5;

/** Post and per-brand sentiment, one choice per post or brand. */
export const SOCIAL_SENTIMENTS = [
  "positive",
  "neutral",
  "negative",
  "mixed",
] as const;
export type SocialSentiment = (typeof SOCIAL_SENTIMENTS)[number];

/** Who gave a post its tags: OpenAI for posts judged before Jev, Jev after. */
export const SOCIAL_JUDGES = ["openai", "jev"] as const;
export type SocialJudge = (typeof SOCIAL_JUDGES)[number];

/** A brand tagged on one post. */
export interface SocialPostBrandTag {
  /** A `SocialBrand.id` of the post's Topic. */
  id: string;
  /** Sentiment toward the brand; only when the brand is present. */
  sentiment?: SocialSentiment;
  /** True when the brand appears in the post's images or video frames. */
  seen: boolean;
}

/** What a post is, as the platform reports it. */
export const SOCIAL_POST_FORMATS = [
  "video",
  "image",
  "carousel",
  "text",
  "link",
] as const;
export type SocialPostFormat = (typeof SOCIAL_POST_FORMATS)[number];

/** Provider fields kept per post: a typed whitelist, never the raw provider item. */
export interface SocialProviderMeta {
  followers?: number | null;
  verified?: boolean | null;
  displayName?: string | null;
  hashtags?: Array<string>;
  format?: SocialPostFormat | null;
  durationSec?: number | null;
  /** Country the platform files the post under (TikTok `region`), ISO 3166 alpha-2. */
  region?: string | null;
  /** The platform marks the post as an ad. */
  isAd?: boolean | null;
  /** The platform marks the post as a paid partnership. */
  paidPartnership?: boolean | null;
  /** The sound the post uses, when the platform names it. */
  music?: SocialPostMusic | null;
  /** The place the author tagged (Instagram), when there is one. */
  location?: SocialPostLocation | null;
  /**
   * The country (ISO 3166-1 alpha-2) the tagged place's point falls in,
   * computed offline at store time; absent without a usable point.
   */
  geoCountry?: string;
  /** The community the post sits in (Reddit), lowercase, without `r/`. */
  subreddit?: string;
  /** The tracker input that found the post: a keyword or handle and its kind. */
  input?: SocialProviderMetaInput;
  /** The provider endpoint or actor that returned the post. */
  endpoint?: string;
  /** The provider's own metric counters, under the provider's field names. */
  rawMetrics?: Record<string, number | null>;
  /** Why the post's `authorHandle` is null, stated by the normalizer; absent when it has one. */
  authorMissing?: SocialAuthorMissing;
  /**
   * The author's provider user or channel id, kept at store so the judge can
   * be told the author is none of the Topic's brand accounts (a typed fact,
   * never a handle compared); absent when the provider gave none.
   */
  authorProviderId?: string;
  /**
   * Server only, never sent to clients: the author's profile picture link as
   * the provider sent it at collect (signed, expires within days). The media
   * step stores it once per creator; the API exposes `authorAvatarPath`.
   */
  authorAvatarUrl?: string;
  /**
   * Server only, never sent to clients: when the creator's picture was stored
   * (ISO time), stamped by the media step on every post of that creator in
   * the org. Its presence is what makes `authorAvatarPath` non-null.
   */
  authorAvatarStoredAt?: string;
}

/** `providerMeta` keys the server keeps for itself and strips from API responses. */
export const SOCIAL_PROVIDER_META_SERVER_KEYS = [
  "authorAvatarUrl",
  "authorAvatarStoredAt",
] as const;

/**
 * A provider author id the creator picture is keyed on: a stored object name
 * and a route segment, so letters, digits, `_` and `-` only.
 */
export const SOCIAL_AUTHOR_AVATAR_PROVIDER_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function isSocialAuthorAvatarProviderId(
  value: string | null | undefined,
): value is string {
  return (
    typeof value === "string" &&
    SOCIAL_AUTHOR_AVATAR_PROVIDER_ID_PATTERN.test(value)
  );
}

/**
 * Why a post has no author handle:
 * - `no_channel`: the provider item carries no channel at all (YouTube
 *   search Shorts);
 * - `no_handle`: its channel has neither a handle nor an id (a YouTube
 *   collaboration listed by title only).
 */
export const SOCIAL_AUTHOR_MISSING = ["no_channel", "no_handle"] as const;
export type SocialAuthorMissing = (typeof SOCIAL_AUTHOR_MISSING)[number];

/** The tracker input a post came from (`kind`: keyword, hashtag, handle, ...). */
export interface SocialProviderMetaInput {
  value: string;
  kind: string;
}

/**
 * One input (keyword or handle, per endpoint) of a sync run: what was asked,
 * what came back and what each filter kept. Bounded: inputs only, no post rows.
 * `judged`, `kept` and `offTopic` are added at judge time.
 */
export interface SocialInputTraceEntry {
  /** The keyword or handle. */
  input: string;
  kind: string;
  endpoint: string;
  /** Request params (preset, sort, region). */
  params: Record<string, string | number | boolean>;
  pages: number;
  credits: number;
  returned: number;
  inWindow: number;
  outOfWindow: number;
  invalid: number;
  /** Posts the tracker already stored. */
  known: number;
  overCap: number;
  /** ISO timestamps of the oldest and newest returned post. */
  oldest: string | null;
  newest: string | null;
  stopReason: string;
  judged?: number;
  kept?: number;
  offTopic?: number;
}

export const SOCIAL_DROP_SAMPLE_REASONS = [
  "out_of_window",
  "language",
  "invalid",
  "over_cap",
  "tombstoned",
  "known_off_topic",
  "judge_off_topic",
] as const;
export type SocialDropSampleReason = (typeof SOCIAL_DROP_SAMPLE_REASONS)[number];

/**
 * One dropped post kept as evidence, at most a few per reason per run. Never
 * the post id or author handle in clear.
 */
export interface SocialDropSample {
  reason: SocialDropSampleReason;
  /** sha256 of the platform post id (`tombstoneHash`). */
  postIdHash: string;
  /** The keyword or handle that found the post; null when unknown. */
  input: string | null;
  /** At most 200 characters of the post text. */
  excerpt: string;
  /** Reason detail: detected vs wanted language, schema reason, ... */
  detail?: string;
  /** Judge score, when judged. */
  score?: number;
}

/** Drop samples of a run, keyed by reason. */
export type SocialDropSamples = Partial<
  Record<SocialDropSampleReason, Array<SocialDropSample>>
>;

export interface SocialPostLocation {
  name?: string;
  lat?: number;
  lng?: number;
}

export interface SocialPostMusic {
  title?: string;
  author?: string;
  /** The post uses its creator's own sound. */
  isOriginal?: boolean;
}

/** Author reach: under 10k, 10k to 100k, 100k to 1M, over 1M followers. */
export const SOCIAL_FOLLOWER_TIERS = [
  "under_10k",
  "10k_100k",
  "100k_1m",
  "over_1m",
] as const;
export type SocialFollowerTier = (typeof SOCIAL_FOLLOWER_TIERS)[number];

/** The follower tier of an author; null when the platform gave no count. */
export function socialFollowerTier(
  followers: number | null | undefined,
): SocialFollowerTier | null {
  if (
    followers === null ||
    followers === undefined ||
    !Number.isFinite(followers) ||
    followers < 0
  ) {
    return null;
  }
  if (followers < 10_000) {
    return "under_10k";
  }
  if (followers < 100_000) {
    return "10k_100k";
  }
  if (followers < 1_000_000) {
    return "100k_1m";
  }
  return "over_1m";
}

/** Where a stored image comes from: the post's cover, a still from its video, a photo, a platform thumbnail. */
export const SOCIAL_IMAGE_KINDS = [
  "cover",
  "frame",
  "photo",
  "thumbnail",
] as const;
export type SocialImageKind = (typeof SOCIAL_IMAGE_KINDS)[number];

/** One stored image of a post, as the API exposes it. */
export interface SocialPostImage {
  kind: SocialImageKind;
  /** 0-based position among the post's stored images; the proxy path's `:index`. */
  index: number;
  /** Seconds into the video, for frames. */
  atSeconds?: number;
  /**
   * Relative path of the authenticated image proxy, without a leading slash
   * (`social-listening/items/<postToken>/images/<index>`). The client prefixes
   * its API base, so the same path works for members and the back office.
   */
  imagePath: string;
}

/** The image proxy path of one stored image; see `SocialPostImage.imagePath`. */
export function socialItemImagePath(postToken: string, index: number): string {
  return `social-listening/items/${encodeURIComponent(postToken)}/images/${index}`;
}

/**
 * The image proxy path of one creator's stored profile picture, relative like
 * `socialItemImagePath` (the client rebases it for the back office).
 */
export function socialAuthorAvatarPath(
  platform: SocialPlatform,
  providerId: string,
): string {
  return `social-listening/authors/${encodeURIComponent(platform)}/${encodeURIComponent(providerId)}/avatar`;
}

/**
 * Market fit (social market fit plan, CD4/CD5): whether a post belongs to its
 * Topic's market, decided at read time by one SQL rule. A Topic with no
 * market and no languages has no verdict (`unknown`, reason `no_market`).
 */
export type SocialMarketFit = "in_market" | "off_market" | "unknown";

/** Why a post got its verdict: the evidence family the tooltip names. */
export type SocialMarketFitReason =
  | "subreddit"
  | "author_account"
  | "author_channel"
  | "author_geotags"
  | "author_bio"
  | "post_geotag"
  | "abroad"
  | "judge_no"
  | "judged"
  | "judge_country"
  | "author_judge_country"
  | "language"
  | "none"
  | "no_market";

/**
 * The market filter of a Topic read (CD7). Absent: every post, as the Topic
 * page opens. `all` states the same. `inAndUnknown` is the page's market
 * filter (named after the Topic's country). `off` lists the posts from other markets (or, on a languages-only
 * Topic, in other languages), for the Assistant. A Topic with no market and
 * no languages keeps every post under every value. Hidden posts are a
 * separate axis.
 */
export const SOCIAL_MARKET_FILTERS = ["inAndUnknown", "in", "off", "all"] as const;
export type SocialMarketFilter = (typeof SOCIAL_MARKET_FILTERS)[number];

/** The market fit of one post, as the Topic reads return it. */
export interface SocialPostMarketFields {
  marketFit: SocialMarketFit;
  marketReason: SocialMarketFitReason;
  /** The country (ISO2) that decided the verdict, or null. */
  marketCountry: string | null;
}

/** Posts of a read by verdict, under every filter but the market one. */
export interface SocialMarketCounts {
  inMarket: number;
  unknown: number;
  offMarket: number;
}

/** A post as the Topic view lists it: the Topics fields are always present. */
export interface SocialTopicPostItem extends SocialPostItem {
  /** Market fit (absent on servers before it); see SocialPostMarketFields. */
  marketFit?: SocialMarketFit;
  marketReason?: SocialMarketFitReason;
  marketCountry?: string | null;
  brands: Array<SocialPostBrandTag>;
  sentiment: SocialSentiment | null;
  providerMeta: SocialProviderMeta;
  followerTier: SocialFollowerTier | null;
  images: Array<SocialPostImage>;
  judgedBy: SocialJudge | null;
  hidden: boolean;
  /** The channel (tracker) that collected the post. */
  trackerToken: string;
  /**
   * The three fields below come with collection v2; absent on servers before
   * it. Who published or paid for the post, as the judge answered; null when
   * unknown (and on Topics asked the v3 question). Never defaults to organic.
   */
  source?: SocialPostSource | null;
  /** Kept comments under the post; null when its comments were not read (never 0 for unread). */
  commentsCollected?: number | null;
  /** Why its comments were not read; null when they were, or when the post has no comment lifecycle. */
  commentsNotRead?: SocialCommentsNotRead | null;
  /** When the post's comments were read (once, when the post was found); null when not read. */
  commentsReadAt?: string | null;
}

export interface SocialUniversePostsResponse {
  posts: Array<SocialTopicPostItem>;
  nextCursor: string | null;
}

/**
 * Where a run is. Sync ends at insert (`collect`), then images are stored
 * (`media`), Jev tags the posts (`judge`), relevant posts are written to the
 * Library (`digest`), and the run closes (`done`). Internal: the UI shows
 * "Collecting" or "Sorting", never the phase names.
 */
export const SOCIAL_RUN_PHASES = [
  "collect",
  "media",
  "judge",
  "digest",
  "done",
] as const;
export type SocialRunPhase = (typeof SOCIAL_RUN_PHASES)[number];

/**
 * One channel of a Topic, as the client sends it: typed searches, hashtags
 * and communities (collection v2), or, for one release (P9), the old wire.
 *
 * - **Typed:** `searches` is `{id?, query}[]` and `hashtags` and
 *   `communities` are their own fields; no value decides its route, so a
 *   search starting with `#` is refused (`hashtag_is_a_source`). Together the
 *   searches and hashtags are 1 to SOCIAL_MAX_KEYWORDS.
 * - **Old wire (one release):** `searches` is `string[]`, a `#tag` entry on a
 *   channel that follows hashtags is that hashtag, and Reddit `handles` are
 *   communities that narrow the channel (`socialTypedChannelInputs`, the
 *   migration's transform). `hashtags` and `communities` are then absent.
 *
 * Every search must be one the channel's search rules take, or the save is a
 * 400. `handles` on TikTok, Instagram and YouTube are the channel's accounts
 * on either wire (they have no typed home yet); on Reddit the typed wire
 * sends `communities` instead.
 */
export interface SocialChannelInput {
  platform: SocialPlatform;
  /**
   * Absent (the one auto-shaped list the wizard edits, matrix row 6): the
   * channel's searches derive from the Topic's `keywords`, shaped per
   * channel by the server.
   */
  searches?: Array<SocialChannelSearchInput> | Array<string>;
  /** Typed wire only; channels whose search rules follow hashtags (`search.hashtags: feed`). */
  hashtags?: Array<SocialChannelHashtagInput>;
  /** Typed wire only; channels whose search rules take communities. */
  communities?: Array<SocialChannelCommunityInput>;
  /** Accounts to collect from (TikTok, Instagram, YouTube); on the old wire also Reddit communities. */
  handles?: Array<string>;
}

/** A channel search as the client sends it: `id` of a search the channel has (or Suggest gave), absent for a new one. */
export interface SocialChannelSearchInput {
  id?: string;
  query: string;
}

/**
 * A hashtag as the client sends it, written with or without the `#`. `check`
 * is the one a source check returned (Suggest); absent, the server keeps the
 * stored check of the same id and tag, else the tag is unchecked.
 */
export interface SocialChannelHashtagInput {
  id?: string;
  tag: string;
  check?: SocialSourceCheck;
}

/** A community as the client sends it; `check` as on a hashtag. */
export interface SocialChannelCommunityInput {
  id?: string;
  name: string;
  mode: SocialCommunityMode;
  check?: SocialSourceCheck;
}

/**
 * What a Topic listens for. Every channel (one tracker per platform) copies
 * this config, except what it searches: each channel has its own `searches`.
 */
export interface SocialUniverseInput {
  name: string;
  /** Subject terms in plain words: what the Topic is about, read by keyword notes and display; never searched. */
  keywords: Array<string>;
  keywordNotes?: Array<SocialKeywordNote>;
  brief?: SocialTrackerBrief;
  labels?: Array<SocialTrackerLabel>;
  /** Themes (internal `topics`), at most SOCIAL_MAX_TOPICS; they sort posts, never decide what is collected. */
  topics?: Array<SocialTopicInput>;
  /** At most SOCIAL_MAX_BRANDS. */
  brands?: Array<SocialBrandInput>;
  /** What the Topic is about (D24): sets the topic group's budget share. */
  subjectKind: SocialSubjectKind;
  /** At least one; one per platform. */
  channels: Array<SocialChannelInput>;
  /** BCP-47, at least one; a post in any of them is kept. */
  languages: Array<string>;
  market: string;
  /** As `SocialTrackerInput.marketCode`: the picked country's code, or null; absent derives it from `market`. */
  marketCode?: string | null;
  depthDays: SocialDepthDays;
  cadence: SocialTrackerCadence;
  runPostLimit: SocialRunPostLimit;
  /** Applies to later first collections only; defaults to SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT (or the saved value). */
  firstCollectionPostLimit?: SocialFirstCollectionPostLimit;
}

/**
 * A Topic edit. `channels`, when sent, is the full new list: missing platforms
 * are removed (their posts and images go), new ones start their first update.
 * A change to brief, themes or brands re-tags the Topic's posts (only changed tags are asked again).
 */
export interface SocialUniverseUpdate {
  name?: string;
  keywords?: Array<string>;
  keywordNotes?: Array<SocialKeywordNote>;
  brief?: SocialTrackerBrief;
  labels?: Array<SocialTrackerLabel>;
  topics?: Array<SocialTopicInput>;
  brands?: Array<SocialBrandInput>;
  subjectKind?: SocialSubjectKind;
  channels?: Array<SocialChannelInput>;
  languages?: Array<string>;
  /** Sent with `marketCode`: a market sent without one has no country code. */
  market?: string;
  marketCode?: string | null;
  /** Applies to new channels' first update only. */
  depthDays?: SocialDepthDays;
  cadence?: SocialTrackerCadence;
  runPostLimit?: SocialRunPostLimit;
  /** Applies to later first collections only; defaults to SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT (or the saved value). */
  firstCollectionPostLimit?: SocialFirstCollectionPostLimit;
}

/** One channel of a Topic as the API returns it; status and counts are per channel. */
export interface SocialUniverseChannel {
  /** The channel's tracker; pause, resume, sync and runs stay per channel. */
  trackerToken: string;
  platform: SocialPlatform;
  /** What the channel searches for. */
  searches: Array<string>;
  /**
   * What the channel also searches for the Topic's brands: each search of
   * each collected brand the channel takes; empty on a channel that does not
   * search brands.
   */
  brandSearches: Array<string>;
  /** Searches of collected brands this channel does not take, each with its reason. */
  brandSearchesSkipped: Array<SocialBrandSearchSkip>;
  handles: Array<string>;
  /**
   * The channel's typed inputs, each with its stable id and check: what the
   * typed wire sends back. `searches` and `handles` above are their old-wire
   * mirror for one release. Absent on servers before it.
   */
  inputs?: SocialTypedChannelInputs;
  status: SocialTrackerStatus;
  /** Why the status is `partial` or `failed`, when typed; absent on older servers. */
  statusReason?: SocialChannelStatusReason | null;
  /** With `not_sorted`: the final judge ask failed, nothing retries until a new update. */
  sortingStopped?: boolean;
  /** Derived: last success older than 2x the cadence; never for `on_demand`. */
  stale: boolean;
  lastError: string | null;
  lastSyncedAt: string | null;
  lastAttemptAt: string | null;
  /** The channel's listed posts published in the Topic's `period` (D23). */
  totalPosts: number;
  consecutiveFailures: number;
  /** Phase of the channel's open run; null when no run is open. */
  phase: SocialRunPhase | null;
  /**
   * Wallet and run-limit fields (credits as decimals); absent on servers
   * before the wallet. Why the channel is paused; null when not paused.
   */
  pauseReason?: SocialTrackerPauseReason | null;
  /** `creditsPausedAt` + SOCIAL_CREDIT_PAUSE_RETRY_DAYS; null unless paused `out_of_credits`. */
  autoResumeEndsAt?: string | null;
  /** Credits charged for this channel's runs this UTC month. */
  creditsThisMonth?: number;
  /** Relevant posts this channel added this UTC month (billed posts). */
  postsKeptThisMonth?: number;
  /** Credits set aside for this channel's open run; 0 when none. */
  creditsHeld?: number;
  /** Whole credits one update of this channel can hold (its hold ceiling). */
  maxCreditsPerUpdate?: number;
  /** When "Update now" is allowed again (cooldown or shortest interval); null when allowed now. */
  manualUpdateAvailableAt?: string | null;
  /**
   * The four fields below come with collection v2; absent on servers before
   * it. The latest closed run's typed outcome; null before any run closed.
   */
  lastRun?: SocialChannelLastRun | null;
  /** The open run's progress; null when no run is open. */
  progress?: SocialChannelProgress | null;
  /** Per collected brand id: its routes, what was skipped and why, and what it got. */
  brandCoverage?: Record<string, SocialBrandCoverage>;
  /**
   * The next run's plan, computed by the server from the channel's source
   * state with `socialChannelRunPlan` (the same plan the estimate and the claim price).
   */
  nextPlan?: SocialChannelRunPlan | null;
}

/** Posts per sentiment; posts with no sentiment (judged before Jev) are not counted. */
export interface SocialSentimentCounts {
  positive: number;
  neutral: number;
  negative: number;
  mixed: number;
}

export interface SocialUniverseItem {
  token: string;
  name: string;
  keywords: Array<string>;
  keywordNotes: Array<SocialKeywordNote>;
  brief: SocialTrackerBrief;
  labels: Array<SocialTrackerLabel>;
  topics: Array<SocialTopic>;
  brands: Array<SocialBrand>;
  /** What the Topic is about (D24); `category` for Topics saved before it. */
  subjectKind: SocialSubjectKind;
  languages: Array<string>;
  market: string;
  /** The market's country code; null when it has none. */
  marketCode: string | null;
  depthDays: SocialDepthDays;
  /**
   * The Topic's period (owner D23), the same for every channel: from the
   * first import's start (creation minus `depthDays`) to now. Every count,
   * chart, mood and digest reads activity dated inside it; posts published
   * before it are earlier context, listed but never counted.
   */
  period: SocialWindow;
  cadence: SocialTrackerCadence;
  runPostLimit: SocialRunPostLimit;
  /** Most relevant posts per channel a charged first collection keeps; those past it wait for the run's unlock (SOCIAL_FIRST_COLLECTION_POST_LIMITS). */
  firstCollectionPostLimit: SocialFirstCollectionPostLimit;
  /** One per platform, derived from the Topic's trackers. */
  channels: Array<SocialUniverseChannel>;
  /** Listed posts (relevant, not hidden) published in `period`, across channels; `sentiment`, reach, creators and the 7-day counts read the same posts. */
  totalPosts: number;
  /** Collected posts still waiting for images or tags ("Sorting N new posts"). */
  postsPending: number;
  sentiment: SocialSentimentCounts;
  /** Summed views of the listed posts reporting views; null when none does. */
  reach?: number | null;
  /** Of the listed posts, those reporting views. */
  reachPosts?: number;
  /** Distinct authors among the listed posts. */
  creators?: number;
  /** Listed posts published in the last 7 days, and in the 7 days before (the card's trend). */
  postsLast7Days?: number;
  postsPrevious7Days?: number;
  /** Up to 4 image proxy paths for the Topic card. */
  previewImagePaths: Array<string>;
  /** Most recent successful update of any channel. */
  lastSyncedAt: string | null;
  /** Knowledge collection holding the Topic's digests; scopes "Ask". */
  collectionToken: string | null;
  retentionDays: number;
  createdAt: string;
  /**
   * Wallet fields (production limits + credits UI plans); absent on servers
   * before the wallet. Credits are decimals (millicredits / 1000), ceilings
   * whole.
   *
   * `out_of_credits` when any channel not paused by the user is paused for
   * credits, else `ops` when any is paused by ops, else `user` when every
   * channel is user-paused, else null.
   */
  pauseReason?: SocialTrackerPauseReason | null;
  /** Earliest `autoResumeEndsAt` across the channels paused `out_of_credits`; null when none. */
  autoResumeEndsAt?: string | null;
  /** The Topic payer's funds against one update of this Topic: low = fewer than SOCIAL_FUNDS_LOW_UPDATES updates. */
  fundsState?: SocialFundsState;
  /** Whole credits one update of every channel can hold: ceil(sum of channel holds / 1000). */
  maxCreditsPerUpdate?: number;
  creditsThisMonth?: number;
  /** Posts added this UTC month across channels (billed posts). */
  postsKeptThisMonth?: number;
  /** Credits set aside for the Topic's open runs. */
  creditsHeld?: number;
  /** Earliest moment "Update all channels now" is allowed again; null when allowed now. */
  manualUpdateAvailableAt?: string | null;
  /** Who pays (the creator's seat, else the org owner's; or the pool); null when unresolved. */
  payer?: SocialCreditPayer | null;
}

export interface SocialUniversesResponse {
  /** Same meaning as `SocialTrackersResponse.enabled`. */
  enabled: boolean;
  /** OWNER or ADMIN: may create, edit and delete Topics and hide or remove posts. */
  canManage: boolean;
  schedule: SocialSyncSchedule;
  /** Same meaning as `SocialTrackersResponse.instagramKeywordSearch`. */
  instagramKeywordSearch?: boolean;
  universes: Array<SocialUniverseItem>;
  /** Back-office reads only: the org's `socialListening` flag. */
  orgFlagEnabled?: boolean;
}

/** A run in a Topic's Activity: a channel's run with its platform. */
export interface SocialUniverseRunItem extends SocialSyncRunItem {
  trackerToken: string;
  platform: SocialPlatform;
}

/** A run's offer (`SocialSyncRunItem.heldOffer`): credits as a decimal, millicredits / 1000. */
export interface SocialRunHeldOffer {
  posts: number;
  credits: number;
}

/** Accepting a run's offer; a retry reads 0 and 0. */
export interface SocialRunUnlockResponse {
  postsAdded: number;
  creditsCharged: number;
}

export interface SocialUniverseRunsResponse {
  runs: Array<SocialUniverseRunItem>;
}

/** "Suggest" in the New topic dialog: drafts searches per channel, keyword notes, themes and brands. */
export interface SocialUniverseSuggestionRequest {
  name?: string;
  /** Subject terms the client already typed, a hint only; may be empty: Suggest proposes the subject (D21b). */
  keywords: Array<string>;
  /** The channels to write searches for; absent: every platform. */
  channels?: Array<SocialPlatform>;
  market?: string;
  /** The market's ISO 3166-1 alpha-2 code, when it has one: Suggest's web search is located there (D25). */
  marketCode?: string | null;
  languages?: Array<string>;
  /** What the client wants to learn: what Suggest reads (D21b). */
  goal: string;
}

/**
 * Whether Suggest's one model call searched the web (D25): `searched` at
 * least one search completed; `search_failed` it tried and none completed;
 * `not_searched` it answered without one. Stated with every suggestion and
 * logged, never folded into another outcome.
 */
export const SOCIAL_SUGGEST_RESEARCH = [
  "searched",
  "search_failed",
  "not_searched",
] as const;
export type SocialSuggestResearch = (typeof SOCIAL_SUGGEST_RESEARCH)[number];

/**
 * Why "Suggest" could not suggest a Topic (the 503's `reason`):
 * `provider_error` the model call failed; `unusable_output` the model's JSON
 * did not parse to the contract; `channel_without_searches` a requested
 * channel got no search it takes; `brand_subject_without_brands` the model
 * said the subject is a brand and named none; `paused` social listening is
 * paused (kill switch or global breaker); `spend_cap` the org reached its
 * monthly spend limit. The tracker Suggest answers the last two as well.
 */
export const SOCIAL_UNIVERSE_SUGGESTION_FAILURES = [
  "provider_error",
  "unusable_output",
  "channel_without_searches",
  "brand_subject_without_brands",
  "paused",
  "spend_cap",
] as const;
export type SocialUniverseSuggestionFailure =
  (typeof SOCIAL_UNIVERSE_SUGGESTION_FAILURES)[number];

/** Most hashtags or communities Suggest proposes per channel. */
export const SOCIAL_SUGGESTED_SOURCES_MAX = 5;

/** Most keywords `SocialUniverseSuggestion.keywords` carries. */
export const SOCIAL_SUGGESTED_KEYWORDS_MAX = 10;

/**
 * A keyword that commonly matches posts about something else (a song, a
 * person, a place, a brand of another category).
 */
export interface SocialKeywordWarning {
  keyword: string;
  /** What else the keyword usually refers to, in one short sentence. */
  reason: string;
}

export interface SocialUniverseSuggestion {
  /**
   * Subject terms to add to the Topic, in plain words, the supplied ones
   * excluded: at most SOCIAL_SUGGESTED_KEYWORDS_MAX. Absent on servers before it.
   */
  keywords?: Array<string>;
  /**
   * Per requested channel, at most SOCIAL_SUGGESTED_KEYWORDS_MAX searches the
   * channel takes; a channel the request did not name is absent.
   */
  searches: Partial<Record<SocialPlatform, Array<string>>>;
  /**
   * Supplied or suggested keywords likely to collide with other subjects;
   * each `keyword` is one of them. Absent on servers before it.
   */
  keywordWarnings?: Array<SocialKeywordWarning>;
  keywordNotes: Array<SocialKeywordNote>;
  topics: Array<SocialTopicInput>;
  brands: Array<SocialBrandInput>;
  /** What the subject is (D24): the form fills its subject kind with it. Absent on servers before it. */
  subjectKind?: SocialSubjectKind;
  /**
   * The drafted topic description: `include` covers the whole subject the
   * keywords search and the client's goal, `exclude` the other meanings the
   * keyword warnings found (may be empty). Absent on servers before it, or
   * when the model returned none.
   */
  brief?: SocialTrackerBrief;
  /**
   * Suggested brands that were not kept: a duplicate by exact normalised name
   * or alias (`normalizeSocialBrandName`) of a brand listed before it. Absent
   * on servers before it.
   */
  refusedBrands?: Array<SocialRefusedBrand>;
  /**
   * What the brand list is: brands proposed, a category-only Topic, or none
   * usable. Absent on servers before it.
   */
  brandsOutcome?: SocialSuggestedBrandsOutcome;
  /**
   * Per requested channel that follows hashtags, at most
   * SOCIAL_SUGGESTED_SOURCES_MAX hashtags, each `unchecked`: Suggest makes no
   * provider call, the form checks its proposals through
   * `POST /social-listening/universes/sources/check`. Absent on servers before it.
   */
  hashtags?: Partial<Record<SocialPlatform, Array<SocialChannelHashtag>>>;
  /** Per requested channel that takes communities, as `hashtags`; mode `add`. */
  communities?: Partial<Record<SocialPlatform, Array<SocialChannelCommunity>>>;
  /** Whether the call searched the web (D25). Absent on servers before it. */
  research?: SocialSuggestResearch;
}

/* ---------------------------- Read layer -------------------------------- */

/** Theme filter and count key for relevant posts with no theme ("Other"). */
export const SOCIAL_OTHER_THEME_ID = "other";

/** A count cell under this is flagged `small`. */
export const SOCIAL_SMALL_CELL_THRESHOLD = 5;

/**
 * Orders of a Topic's post list. `views` is most reach first: posts without
 * views sort last, and the cursor carries the value. The API default stays
 * `newest`; the Topic page asks for `views`.
 */
export const SOCIAL_TOPIC_POSTS_SORTS = [
  "newest",
  "engagement",
  "views",
] as const;
export type SocialTopicPostsSort = (typeof SOCIAL_TOPIC_POSTS_SORTS)[number];

/**
 * The measure a Topic page reads (client only): `reach` reads the summed
 * `views` of a count, `posts` reads its post count. Every count carries both,
 * so switching never refetches.
 */
export const SOCIAL_TOPIC_METRICS = ["reach", "posts"] as const;
export type SocialTopicMetric = (typeof SOCIAL_TOPIC_METRICS)[number];

/** Longest `q` accepted, after trimming. */
export const SOCIAL_TOPIC_SEARCH_MAX_LENGTH = 100;

/**
 * One author of a Topic's posts. `handle` is matched normalized
 * (`normalizeSocialHandle`: no leading `@`, case-insensitive), so `@Foo` and
 * `foo` are the same author.
 */
export interface SocialTopicAuthorRef {
  platform: SocialPlatform;
  handle: string;
}

/** Filters shared by the post list and the counts; absent means Any. Hidden posts are always excluded. */
export interface SocialTopicFilters {
  /** A `SocialTopic.id` of the Topic, or SOCIAL_OTHER_THEME_ID. */
  themeId?: string;
  /** A `SocialBrand.id` of the Topic. */
  brandId?: string;
  platform?: SocialPlatform;
  sentiment?: SocialSentiment;
  /**
   * Posts the judge labelled with this source (collection v2). Unknown
   * (null) matches no source. Absent on servers before it (they answer 400).
   */
  source?: SocialPostSource;
  /** Posts published inside it (inclusive). */
  window?: SocialWindow;
  /**
   * Search, trimmed, 1 to SOCIAL_TOPIC_SEARCH_MAX_LENGTH characters: a
   * case-insensitive substring of the post text, the author handle or the
   * author display name (`%`, `_` and `\` match literally). HTTP `q`.
   */
  q?: string;
  /** Posts by this author only. HTTP `authorPlatform` + `authorHandle`, both or neither (else 400). */
  author?: SocialTopicAuthorRef;
  /**
   * The post at this link: the asked link and each stored link compare under
   * one normalization (`normalizeSocialPostUrl`). A TikTok short link
   * (`vm.tiktok.com`, `vt.tiktok.com`) is refused (400). Read by the
   * Assistant's Topic reads; the member routes do not take it.
   */
  url?: string;
  /**
   * Market fit filter (SOCIAL_MARKET_FILTERS). Absent: every post. Ignored by
   * the hidden posts list.
   */
  market?: SocialMarketFilter;
}

/** `hidden` of a posts query: only the Topic's hidden posts. */
export const SOCIAL_TOPIC_HIDDEN_ONLY = "only";

export interface SocialTopicPostsQuery {
  filters?: SocialTopicFilters;
  /** Defaults to `newest`; ignored with `hidden`. */
  sort?: SocialTopicPostsSort;
  /**
   * `only`: the Topic's hidden posts instead (each `hidden: true`), most
   * recently hidden first, same cursor paging. Counts never include hidden
   * posts. Absent on servers before it (they answer 400).
   */
  hidden?: typeof SOCIAL_TOPIC_HIDDEN_ONLY;
  cursor?: string;
  limit?: number;
}

export const SOCIAL_TOPIC_COUNT_GROUPS = [
  "theme",
  "brand",
  "sentiment",
  "channel",
  "week",
  "keyword",
  "source",
] as const;
export type SocialTopicCountGroup = (typeof SOCIAL_TOPIC_COUNT_GROUPS)[number];

export interface SocialTopicCountsQuery {
  groupBy: SocialTopicCountGroup;
  window?: SocialWindow;
  filters?: SocialTopicFilters;
  /** Adds `market` to the response (one more scan): only the call that shows it. */
  includeMarket?: boolean;
}

export interface SocialTopicCountCell {
  /**
   * theme: a theme id or SOCIAL_OTHER_THEME_ID; brand: a brand id; sentiment:
   * a SocialSentiment; source: a SocialPostSource (unknown is in no cell);
   * channel: a SocialPlatform; week: the UTC Monday the
   * week starts on, `YYYY-MM-DD`; keyword: the keyword text, one cell per
   * Topic keyword in `keywords` order, counting posts whose text mentions it
   * (accent and case insensitive, whole words) or whose hashtags equal it
   * with spaces removed.
   */
  key: string;
  count: number;
  /** count < SOCIAL_SMALL_CELL_THRESHOLD: too few posts to read as a share. */
  small: boolean;
  /**
   * Reach: the sum of `metrics.views` over the cell's posts that report a
   * numeric view count (a float, a string or a missing value is no view
   * count). Never likes. 0 when no post reports views. Absent on servers
   * before the measures, like every measure field below.
   */
  views?: number;
  /** Posts of the cell that report views: the coverage of `views` ("Views on 77 of 94 posts"). */
  viewsPosts?: number;
}

/**
 * `groupBy=brand` only: posts naming any of the Topic's brands, under the
 * same filters with `brandId` excluded (like the brand facet). The share of
 * voice denominator: a brand's share is its cell over these.
 */
export interface SocialTopicBrandedTotals {
  count: number;
  views: number;
  viewsPosts: number;
}

export interface SocialTopicCountsResponse {
  groupBy: SocialTopicCountGroup;
  /** Posts matching the filters and window. A post with two brands or themes is in two cells, so cells can sum past it. */
  total: number;
  cells: Array<SocialTopicCountCell>;
  /** The window counted: the Topic's period (D23), narrowed by any window asked. */
  window: SocialWindow | null;
  /** Summed views of the `total` posts; see SocialTopicCountCell.views. */
  views?: number;
  /** Posts of `total` that report views. */
  viewsPosts?: number;
  /** Likes + comments + shares of the `total` posts, a missing one counting 0. Never views. */
  engagement?: number;
  /** Distinct authors of the `total` posts, by platform and normalized handle; posts with no handle are not counted. */
  creators?: number;
  /** Only for `groupBy=brand`. */
  branded?: SocialTopicBrandedTotals;
  /**
   * Posts by market fit under every filter but `market`. All `unknown` on a
   * Topic with no market and no languages. Absent on servers before it.
   */
  market?: SocialMarketCounts;
}

/** Time buckets of the Topic series, by post publish date in UTC. */
export const SOCIAL_TOPIC_SERIES_BUCKETS = ["day", "week"] as const;
export type SocialTopicSeriesBucket =
  (typeof SOCIAL_TOPIC_SERIES_BUCKETS)[number];

/** With no `bucket` asked, a window (the Topic's period, narrowed by the filters) of at most this many days is bucketed by day, a longer one by week. */
export const SOCIAL_TOPIC_SERIES_DAY_MAX_SPAN_DAYS = 45;

/** The trend shows once the series has at least this many non-empty buckets. */
export const SOCIAL_TOPIC_TREND_MIN_BUCKETS = 3;

/** Extra splits of the Topic series: `brand` adds a per-brand row set (`SocialTopicSeriesResponse.brands`). */
export const SOCIAL_TOPIC_SERIES_SPLITS = ["brand"] as const;
export type SocialTopicSeriesSplit = (typeof SOCIAL_TOPIC_SERIES_SPLITS)[number];

export interface SocialTopicSeriesQuery {
  filters?: SocialTopicFilters;
  /** Absent: picked from the read's window (SOCIAL_TOPIC_SERIES_DAY_MAX_SPAN_DAYS). */
  bucket?: SocialTopicSeriesBucket;
  /**
   * A `SocialBrand.id` of the Topic (anything else is a 400): adds
   * `brandPosts` and `brandedPosts` to every row, both counted with every
   * filter except `brandId`.
   */
  brandShareOf?: string;
  /** `brand`: also count each of the Topic's brands per bucket, in the same request. */
  split?: SocialTopicSeriesSplit;
}

/**
 * One bucket of one brand (`split=brand`): posts naming the brand under every
 * filter except `brandId` (like the brand facet and share of voice), so a
 * post naming two brands counts in both. `brandId` is a `SocialBrand.id`.
 */
export interface SocialTopicSeriesBrandRow {
  /** First day of the bucket, `YYYY-MM-DD` UTC. */
  start: string;
  brandId: string;
  posts: number;
  /** Summed views of the row's `posts`. */
  views: number;
  /** Posts of the row's `posts` that report views. */
  viewsPosts: number;
}

/** One bucket of one channel. */
export interface SocialTopicSeriesRow {
  /** First day of the bucket, `YYYY-MM-DD` UTC (a Monday for `week`). */
  start: string;
  platform: SocialPlatform;
  /** Posts matching every filter. */
  posts: number;
  positive: number;
  negative: number;
  neutral: number;
  mixed: number;
  /** Only with `brandShareOf`: posts tagged with that brand, ignoring `brandId`. */
  brandPosts?: number;
  /** Only with `brandShareOf`: posts tagged with any of the Topic's brands, ignoring `brandId`. */
  brandedPosts?: number;
  /**
   * Summed views of the row's `posts` (views of posts published in the
   * bucket, as collected). Absent on servers before the measures.
   */
  views?: number;
  /** Posts of the row's `posts` that report views. */
  viewsPosts?: number;
  /**
   * Kept comments published in the bucket (D23: activity at its own date),
   * under listed and context posts matching every filter but the window; a recent comment
   * on an earlier post counts. Not in `posts`, `views` or the sentiments
   * (comments carry no sentiment). Absent on servers before D23.
   */
  comments?: number;
}

export interface SocialTopicSeriesResponse {
  /** The bucket used: the asked one, or the one picked from the window. */
  bucket: SocialTopicSeriesBucket;
  /**
   * The axis: the first and last bucket starts (`YYYY-MM-DD`) of the read's
   * window, the Topic's period narrowed by the filters' window (D23). The
   * client fills every bucket across it. Absent on servers before D23.
   */
  range?: { from: string; to: string };
  /**
   * Oldest first, then channels in SOCIAL_PLATFORMS order. A row exists only
   * where one of its counts is non-zero; the client fills the gaps. Summed
   * `posts` equals the counts `total` under the same filters, and summed
   * `views` / `viewsPosts` equal the counts `views` / `viewsPosts`.
   */
  buckets: Array<SocialTopicSeriesRow>;
  /**
   * Only with `split=brand`: oldest first, then brands in the Topic's order.
   * A row exists only where `posts` is non-zero; the client fills the gaps.
   */
  brands?: Array<SocialTopicSeriesBrandRow>;
}

/** Orders of the Topic authors: most reach (then posts) or most posts (then reach). */
export const SOCIAL_TOPIC_AUTHORS_SORTS = ["views", "posts"] as const;
export type SocialTopicAuthorsSort =
  (typeof SOCIAL_TOPIC_AUTHORS_SORTS)[number];

/** `limit` of the authors query: 1 to this. */
export const SOCIAL_TOPIC_AUTHORS_LIMIT_MAX = 50;
/** `limit` when absent. */
export const SOCIAL_TOPIC_AUTHORS_DEFAULT_LIMIT = 10;

/** `authorSearch` of the authors query: at most this many characters. */
export const SOCIAL_TOPIC_AUTHOR_SEARCH_MAX_LENGTH = 100;

export interface SocialTopicAuthorsQuery {
  filters?: SocialTopicFilters;
  /**
   * Keeps the authors whose handle or display name contains this text (case
   * insensitive). Not the posts search `filters.q`: it narrows the authors
   * only, never the posts they are counted from.
   */
  authorSearch?: string;
  /** Defaults to `views`. */
  sort?: SocialTopicAuthorsSort;
  limit?: number;
}

/**
 * One author of the filtered posts, grouped by platform and normalized
 * handle. Posts with no handle belong to no author.
 */
export interface SocialTopicAuthor {
  platform: SocialPlatform;
  /** As stored on the author's most recent post, without a leading `@`. */
  handle: string;
  displayName: string | null;
  verified: boolean | null;
  posts: number;
  /** Summed views of the author's posts; see SocialTopicCountCell.views. */
  views: number;
  viewsPosts: number;
  /** Likes + comments + shares of the author's posts. */
  engagement: number;
  /** The largest `providerMeta.followers` on the author's posts; null when none carries it. */
  followers: number | null;
  /** The author's posts naming any of the Topic's brands. */
  brandedPosts: number;
  /**
   * The creator's stored profile picture (`socialAuthorAvatarPath`); null
   * when none is stored. The API always sets it; optional for older clients
   * and fixtures.
   */
  authorAvatarPath?: string | null;
}

export interface SocialTopicAuthorsResponse {
  /** Distinct authors under the filters; equals the counts `creators`. */
  total: number;
  /** At most `limit`, in `sort` order. */
  authors: Array<SocialTopicAuthor>;
}

/* ------------------------- Posts meter and credits ---------------------- */

/** Rate: 50 credits per 150 relevant (accepted) posts added (doubled from 25, owner 2026-11-02). */
export const SOCIAL_CREDITS_PER_150_POSTS = 50;
export const SOCIAL_POSTS_PER_CREDIT_BLOCK = 150;

/**
 * Whole credits for posts: `ceil(posts x SOCIAL_CREDITS_PER_150_POSTS / 150)`; 0 for none. Never for
 * per-run billing (it rounds up per call): the wallet uses
 * `socialMillicreditsForPosts` and accrues fractions.
 */
export function socialCreditsForPosts(posts: number): number {
  if (!Number.isFinite(posts) || posts <= 0) {
    return 0;
  }
  return Math.ceil(
    (Math.ceil(posts) * SOCIAL_CREDITS_PER_150_POSTS) /
      SOCIAL_POSTS_PER_CREDIT_BLOCK,
  );
}

/* ------------------------------ Wallet (v4) ------------------------------ */

/** 1 credit = 1,000 millicredits; the wallet computes in millicredits, APIs carry credits as decimals. */
export const SOCIAL_MILLICREDITS_PER_CREDIT = 1000;
/** Cost-plus markup on a run's real cost (default; the server reads SOCIAL_COST_PLUS_MARKUP). */
export const SOCIAL_COST_PLUS_MARKUP = 1.5;
/** USD per credit the cost-plus term converts at (default; server: SOCIAL_CREDIT_PRICE_BASIS_USD). */
export const SOCIAL_CREDIT_PRICE_BASIS_USD = 0.4;
/** Days an out-of-credits pause retries on its own (default; server: SOCIAL_CREDIT_PAUSE_RETRY_DAYS). */
export const SOCIAL_CREDIT_PAUSE_RETRY_DAYS = 30;
/** `fundsState` is `low` when the payer covers fewer than this many updates of the Topic. */
export const SOCIAL_FUNDS_LOW_UPDATES = 3;
/** Wallet billing version persisted on each run at claim; null on a run = pre-wallet, free. */
export const SOCIAL_BILLING_VERSION = 1;

/** Scheduled updates per month by cadence, for "each month" ceilings (weekly = 52 / 12). */
export const SOCIAL_UPDATES_PER_MONTH: Record<
  Exclude<SocialTrackerCadence, "on_demand">,
  number
> = {
  daily: 30,
  weekly: 52 / 12,
};

export const SOCIAL_CREDIT_PAYER_KINDS = ["seat", "pool"] as const;
export type SocialCreditPayerKind = (typeof SOCIAL_CREDIT_PAYER_KINDS)[number];

/** Which term of max(kept, cost-plus) set a run's charge. Back office only. */
export const SOCIAL_CHARGE_BASES = ["kept", "cost_plus"] as const;
export type SocialChargeBasis = (typeof SOCIAL_CHARGE_BASES)[number];

/**
 * A run's wallet state: `free` (charging off, pre-cutover, admin, or a run
 * that ended without a charge), `held` (credits set aside, not settled),
 * `settled` (charged), `unpaid` (the payer could not cover it at settlement:
 * not charged, never collected later), `released` (failed, killed or
 * skipped: everything returned).
 */
export const SOCIAL_RUN_BILLING_STATUSES = [
  "free",
  "held",
  "settled",
  "unpaid",
  "released",
] as const;
export type SocialRunBillingStatus = (typeof SOCIAL_RUN_BILLING_STATUSES)[number];

/** Payer's funds against one update of a Topic (server computed; overage room counts as funds). */
export const SOCIAL_FUNDS_STATES = ["ok", "low", "insufficient"] as const;
export type SocialFundsState = (typeof SOCIAL_FUNDS_STATES)[number];

/** Who pays a Topic's updates, as shown to the viewer; never a row id. */
export interface SocialCreditPayer {
  kind: SocialCreditPayerKind;
  /** The paying seat's user name; null for the pool. */
  displayName: string | null;
  /** True when the paying seat is the viewer's own. */
  isViewer: boolean;
}

/** Typed run-limit and funds errors (HTTP 429 / 402 bodies). */
export const SOCIAL_LIMIT_ERROR_CODES = [
  "social_update_cooldown",
  "social_queue_full",
  "social_out_of_credits",
] as const;
export type SocialLimitErrorCode = (typeof SOCIAL_LIMIT_ERROR_CODES)[number];

export interface SocialLimitErrorBody {
  code: SocialLimitErrorCode;
  message?: string;
  /** Seconds until a retry can succeed (cooldown, queue full); absent on 402. */
  retryAfterSeconds?: number;
}

/** Rounds to 6 decimals before a ceil or floor, so float noise (150.00000000000003) never adds a unit. */
function roundForStep(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** Kept term: `floor(posts x 1000 x 25 / 150)` millicredits (exact rate, rounded down); 0 for none. */
export function socialMillicreditsForPosts(posts: number): number {
  if (!Number.isFinite(posts) || posts <= 0) {
    return 0;
  }
  return Math.floor(
    roundForStep(
      (Math.floor(posts) * SOCIAL_MILLICREDITS_PER_CREDIT * SOCIAL_CREDITS_PER_150_POSTS) /
        SOCIAL_POSTS_PER_CREDIT_BLOCK,
    ),
  );
}

/** Cost-plus term: `ceil(usd x markup / priceBasisUsd x 1000)` millicredits; 0 for no cost. */
export function socialCostPlusMillicredits(
  usd: number,
  markup: number = SOCIAL_COST_PLUS_MARKUP,
  priceBasisUsd: number = SOCIAL_CREDIT_PRICE_BASIS_USD,
): number {
  if (!Number.isFinite(usd) || usd <= 0 || !(priceBasisUsd > 0) || !(markup > 0)) {
    return 0;
  }
  return Math.ceil(
    roundForStep((usd * markup * SOCIAL_MILLICREDITS_PER_CREDIT) / priceBasisUsd),
  );
}

/**
 * Hold for one channel run (D1): max(the run's post cap at the per-post rate,
 * rounded up, and the cost-plus of its worst-case USD). The server hold and
 * the estimate endpoint both call this, so they always agree.
 */
export function socialHoldMillicredits(input: {
  runPostCap: number;
  worstCaseUsd: number;
  markup?: number;
  priceBasisUsd?: number;
}): number {
  const cap = Number.isFinite(input.runPostCap) && input.runPostCap > 0 ? Math.ceil(input.runPostCap) : 0;
  const postTerm = Math.ceil(
    roundForStep(
      (cap * SOCIAL_MILLICREDITS_PER_CREDIT * SOCIAL_CREDITS_PER_150_POSTS) /
        SOCIAL_POSTS_PER_CREDIT_BLOCK,
    ),
  );
  const costTerm = socialCostPlusMillicredits(
    input.worstCaseUsd,
    input.markup ?? SOCIAL_COST_PLUS_MARKUP,
    input.priceBasisUsd ?? SOCIAL_CREDIT_PRICE_BASIS_USD,
  );
  return Math.max(postTerm, costTerm);
}

/**
 * Charge for a succeeded, settled channel run (D2): max(kept term, cost-plus
 * term of `costUsd` = provider cost + apportioned first-judge cost). A tie
 * reports `kept`. Failed, killed and skipped runs are never passed here: they
 * charge 0.
 */
export function socialRunChargeMillicredits(input: {
  postsKept: number;
  costUsd: number;
  markup?: number;
  priceBasisUsd?: number;
}): { millicredits: number; basis: SocialChargeBasis } {
  const kept = socialMillicreditsForPosts(input.postsKept);
  const costPlus = socialCostPlusMillicredits(
    input.costUsd,
    input.markup ?? SOCIAL_COST_PLUS_MARKUP,
    input.priceBasisUsd ?? SOCIAL_CREDIT_PRICE_BASIS_USD,
  );
  return costPlus > kept
    ? { millicredits: costPlus, basis: "cost_plus" }
    : { millicredits: kept, basis: "kept" };
}

/** Millicredits as the API's credit decimal (up to 3 places). */
export function socialMillicreditsToCredits(millicredits: number): number {
  if (!Number.isFinite(millicredits)) {
    return 0;
  }
  return Math.round(millicredits) / SOCIAL_MILLICREDITS_PER_CREDIT;
}

/** Held millicredits as whole credits for a balance (`creditInfo.pending`): rounded up, so `available` stays safe. */
export function socialHeldWholeCredits(millicredits: number): number {
  if (!Number.isFinite(millicredits) || millicredits <= 0) {
    return 0;
  }
  return Math.ceil(millicredits / SOCIAL_MILLICREDITS_PER_CREDIT);
}

/**
 * "Up to" ceilings for a Topic: per update = ceil(sum of its channels' holds
 * / 1000); per month = that whole number x updates per month (daily 30, weekly
 * 52 / 12), rounded up, so the two rows reconcile (34 x 30 = 1,020); null for
 * on demand.
 */
export function socialEstimateCeilings(input: {
  channelHoldsMillicredits: Array<number>;
  cadence: SocialTrackerCadence;
}): { maxCreditsPerUpdate: number; maxCreditsPerMonth: number | null } {
  const total = input.channelHoldsMillicredits.reduce(
    (sum, value) => sum + (Number.isFinite(value) && value > 0 ? value : 0),
    0,
  );
  const maxCreditsPerUpdate = Math.ceil(total / SOCIAL_MILLICREDITS_PER_CREDIT);
  if (input.cadence === "on_demand") {
    return { maxCreditsPerUpdate, maxCreditsPerMonth: null };
  }
  return {
    maxCreditsPerUpdate,
    maxCreditsPerMonth: Math.ceil(
      roundForStep(maxCreditsPerUpdate * SOCIAL_UPDATES_PER_MONTH[input.cadence]),
    ),
  };
}

/** Funds against one update: insufficient below one, low below SOCIAL_FUNDS_LOW_UPDATES, else ok. */
export function socialFundsState(
  availableMillicredits: number,
  perUpdateMillicredits: number,
): SocialFundsState {
  const perUpdate = Math.max(0, perUpdateMillicredits);
  if (availableMillicredits < perUpdate) {
    return "insufficient";
  }
  if (availableMillicredits < perUpdate * SOCIAL_FUNDS_LOW_UPDATES) {
    return "low";
  }
  return "ok";
}

/** End of an out-of-credits pause's auto-resume window (ISO); null when not paused for credits. */
export function socialAutoResumeEndsAt(
  creditsPausedAt: Date | string | null | undefined,
  retryDays: number = SOCIAL_CREDIT_PAUSE_RETRY_DAYS,
): string | null {
  if (!creditsPausedAt) {
    return null;
  }
  const start = new Date(creditsPausedAt).getTime();
  if (!Number.isFinite(start)) {
    return null;
  }
  return new Date(start + retryDays * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * `POST /social-listening/universes/estimate`: the Topic dialog's draft as it
 * stands (the fields create or edit would send that size a run). The server
 * derives each channel's sources and plans them with `socialChannelRunPlan`
 * exactly as the run does, so the posts, the credits, the funds check and
 * the claim's hold read one plan.
 */
export interface SocialEstimateRequest {
  /** The saved Topic the dialog edits; absent on create. Its channels' source state sizes their next run. */
  universeToken?: string;
  keywords?: Array<string>;
  /** At least one; one per platform. */
  channels: Array<SocialChannelInput>;
  brands?: Array<SocialBrandInput>;
  subjectKind: SocialSubjectKind;
  /** The picked country's code, or null; absent keeps the saved Topic's. */
  marketCode?: string | null;
  depthDays: SocialDepthDays;
  cadence: SocialTrackerCadence;
  runPostLimit: SocialRunPostLimit;
  /** Applies to later first collections only; defaults to SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT (or the saved value). */
  firstCollectionPostLimit?: SocialFirstCollectionPostLimit;
}

/** The dialog's posts and ceilings, each channel's from its run plan, priced with the claim's hold function. */
export interface SocialEstimateResponse {
  /** Whole credits. */
  maxCreditsPerUpdate: number;
  /** Whole credits; null for on demand. */
  maxCreditsPerMonth: number | null;
  /**
   * The viewer as payer of the Topic (their seat, the org owner's, or the
   * pool) against its next run then updates; null when no payer resolves.
   * `insufficient` blocks Create (never an edit).
   */
  fundsState: SocialFundsState | null;
  /** Per channel: its next run and each later update. */
  channels: Array<SocialEstimateChannel>;
  /** Whole credits the next run of every channel can hold (on create: the first collection). */
  maxCreditsNextRun: number;
  /**
   * Whole credits of the next run's imports across channels (the first
   * collection on create), rounded up once on the total; null when no
   * channel imports.
   */
  maxCreditsImports: number | null;
}

/** One first import a channel's next run makes, with its look-back and its share of the run's target. */
export interface SocialEstimateImport {
  /** The brand it imports, by name; null for the channel's own searches (the topic group). */
  brandName: string | null;
  readDays: number;
  /** Relevant posts it aims for. */
  posts: number;
}

/** One channel of the estimate: relevant posts and the credits they can hold. */
export interface SocialEstimateChannel {
  platform: SocialPlatform;
  /** The channel's next run as the draft stands: what the claim would hold. */
  nextRun: {
    /** Relevant posts it aims for (the plan's target, every brand's floor included). */
    posts: number;
    /** Whole credits. */
    maxCredits: number;
    /** A channel with nothing imported yet: its first import's look-back; null otherwise. */
    firstImport: { readDays: number } | null;
    /** On a channel already imported: the first imports its next run adds (a new brand or search). */
    addedImports: Array<SocialEstimateImport>;
    /**
     * Whole credits of the next run's imports: the whole next run when the
     * channel imports for the first time (`firstImport`), else the next run
     * less a later update (floored at 0), what its `addedImports` add.
     */
    importCredits: number;
  };
  /** Each later update: every source reads the cadence's interval (on demand, the days since the last update, else one week), or from its saved cursor when older. */
  perUpdate: {
    posts: number;
    /** Whole credits. */
    maxCredits: number;
  };
}

/** The org's social wallet this UTC month (replaces the allowance summary). */
export interface SocialWalletUsageSummary {
  /** UTC month, `YYYY-MM`. */
  month: string;
  /** Credits charged this month (decimal; whole credits are debited as fractions add up). */
  creditsSpent: number;
  /** Posts added this month (billed posts); admin and test syncs excluded. */
  postsKept: number;
  /** Credits set aside for open runs now. */
  creditsHeld: number;
  /** Credits per added post (25 / 150). */
  perPostCredits: number;
  /** The viewer's payer; null when unresolved. */
  payer: SocialCreditPayer | null;
}

/** Back-office view: the same plus our cost and margin. Never in customer responses. */
export interface SocialAdminWalletUsageSummary extends SocialWalletUsageSummary {
  /** Provider plus Jev spend this month, in USD. */
  internalCostUsd: number;
  /** Revenue at the billing events' real rate minus internal cost, in USD. */
  marginUsd: number;
  /** Credits not collected because the payer ran out at settlement. */
  creditsUnpaid: number;
  /** Settled runs whose charge came from the cost-plus term. */
  costPlusRuns: number;
  /** Effective internal monthly spend cap (anomaly guard), in USD. */
  monthlyCapUsd: number;
}

/** Back office: an org's infra-limit overrides (spend cap only). */
export interface SocialOrgLimits {
  /** The back-office override; null uses the env default. */
  monthlyCapUsdOverride: number | null;
  defaultMonthlyCapUsd: number;
  effectiveMonthlyCapUsd: number;
}

/** Back office `PUT .../social-limits`: null clears the override. */
export interface UpdateSocialOrgLimitsRequest {
  monthlyCapUsd: number | null;
}

/** Back office: the global runtime switches row. */
export interface SocialRuntimeSwitches {
  killSwitchOn: boolean;
  killSwitchReason: string | null;
  killSwitchChangedAt: string | null;
  /** Row override; null uses the env default. */
  globalDailyBreakerUsdOverride: number | null;
  defaultGlobalDailyBreakerUsd: number;
  effectiveGlobalDailyBreakerUsd: number;
  updatedAt: string | null;
}

/** Back office `PUT .../social-runtime-switches`: omitted fields stay; null clears an override. */
export interface UpdateSocialRuntimeSwitchesRequest {
  killSwitchOn?: boolean;
  killSwitchReason?: string | null;
  globalDailyBreakerUsd?: number | null;
}

/** Back office: one org's day in the cost rollup (USD and credits as decimals). */
export interface SocialOrgCostDay {
  /** UTC day, `YYYY-MM-DD`. */
  day: string;
  providerUsd: number;
  jevFirstJudgeUsd: number;
  jevRejudgeUsd: number;
  runs: number;
  failedRuns: number;
  fallbackRuns: number;
  postsFetched: number;
  postsKept: number;
  creditsHeld: number;
  creditsCharged: number;
  creditsUnpaid: number;
  keptBasisRuns: number;
  costPlusBasisRuns: number;
  revenueUsd: number;
  marginUsd: number;
  vendorThrottles: number;
}

export interface SocialOrgCostDaysResponse {
  days: Array<SocialOrgCostDay>;
}

/**
 * @deprecated Allowance model, replaced by `SocialWalletUsageSummary`. Kept
 * only until its consumers move (PL Step 5, UI Steps 2, 4 and 6); delete then.
 * The org's posts this UTC month against its included posts.
 */
export interface SocialPostsUsageSummary {
  /** UTC month, `YYYY-MM`. */
  month: string;
  includedPosts: number;
  /** Provider-charged posts this month, shown or not; admin and test syncs excluded. */
  postsFetched: number;
  overPosts: number;
  overCredits: number;
  /** ISO start of the next UTC month. */
  resetsAt: string;
}

/** @deprecated Allowance model; see `SocialAdminWalletUsageSummary`. */
export interface SocialAdminPostsUsageSummary extends SocialPostsUsageSummary {
  /** Per-org included posts set in the back office; null uses the tier default. */
  override: number | null;
  tierDefault: number;
  /** Provider (ScrapeCreators) plus Jev spend this month, in USD. */
  internalCostUsd: number;
}

/** @deprecated Allowance override, removed with the allowance (PL Step 5). */
export interface UpdateSocialPostsAllowanceRequest {
  includedPosts: number | null;
}

/** @deprecated Allowance model. Builds a usage summary from the month's totals. */
export function summarizeSocialPostsUsage(input: {
  month: string;
  includedPosts: number;
  postsFetched: number;
  resetsAt: string;
}): SocialPostsUsageSummary {
  const overPosts = Math.max(0, input.postsFetched - input.includedPosts);
  return {
    month: input.month,
    includedPosts: input.includedPosts,
    postsFetched: input.postsFetched,
    overPosts,
    overCredits: socialCreditsForPosts(overPosts),
    resetsAt: input.resetsAt,
  };
}

/**
 * Topics HTTP contract (org-scoped, JWT cookie; tokens only, never row ids):
 *   GET    /social-listening/universes                       -> SocialUniversesResponse
 *   POST   /social-listening/universes           (manage)    SocialUniverseInput -> SocialUniverseItem
 *   POST   /social-listening/universes/suggest   (manage)    SocialUniverseSuggestionRequest -> SocialUniverseSuggestion
 *   GET    /social-listening/universes/:token                -> SocialUniverseItem
 *   PATCH  /social-listening/universes/:token    (manage)    SocialUniverseUpdate -> SocialUniverseItem
 *   DELETE /social-listening/universes/:token    (manage)    digests, collection, channels, images -> 204
 *   POST   /social-listening/universes/:token/sync (manage)  every channel -> SocialUniverseItem
 *   Filters (<filters>): themeId=&brandId=&platform=&sentiment=&from=&to=&q=&authorPlatform=&authorHandle=
 *   GET    /social-listening/universes/:token/posts?<filters>&sort=newest|engagement|views&cursor=&limit=
 *                                                            -> SocialUniversePostsResponse
 *   GET    /social-listening/universes/:token/counts?groupBy=&<filters>
 *                                                            -> SocialTopicCountsResponse
 *   GET    /social-listening/universes/:token/series?<filters>&bucket=&brandShareOf=
 *                                                            -> SocialTopicSeriesResponse
 *   GET    /social-listening/universes/:token/authors?<filters>&sort=views|posts&limit=1..50
 *                                                            -> SocialTopicAuthorsResponse
 *   GET    /social-listening/universes/:token/runs           -> SocialUniverseRunsResponse
 *   POST   /social-listening/universes/:token/runs/:runToken/unlock -> SocialRunUnlockResponse
 *   POST   /social-listening/posts/:token/hide   (manage)    -> 204
 *   POST   /social-listening/posts/:token/unhide (manage)    -> 204
 *   GET    /social-listening/items/:token/images/:index      -> image/jpeg (org-checked byte proxy)
 *   GET    /social-listening/usage                           -> SocialWalletUsageSummary
 *   POST   /social-listening/universes/estimate  (SocialEstimateRequest)
 *                                                            -> SocialEstimateResponse
 *   POST   .../universes, .../universes/:token/sync, .../trackers/:token/sync|resume
 *          may answer 429 { code: social_update_cooldown | social_queue_full, retryAfterSeconds }
 *          or 402 { code: social_out_of_credits } (SocialLimitErrorBody); edits never do
 *   PATCH  /social-listening/trackers/:token  on a Topic's channel -> 409 ("Edit the topic")
 *   DELETE /social-listening/trackers/:token  on a Topic's channel -> removes the channel; the last one deletes the Topic
 * Back office mirrors the reads, posts, images, sync and delete under its admin base, plus
 *   GET    .../usage -> SocialAdminWalletUsageSummary
 *   GET    .../social-limits -> SocialOrgLimits;  PUT .../social-limits  UpdateSocialOrgLimitsRequest -> SocialOrgLimits
 *   GET    .../social-cost-days?from=&to= -> SocialOrgCostDaysResponse
 *   GET    /social-runtime-switches -> SocialRuntimeSwitches;  PUT  UpdateSocialRuntimeSwitchesRequest -> SocialRuntimeSwitches
 */

/* ------------------------ Silent tag registry (F3) ------------------------ */

/**
 * Silent tags (Phase 1b follow-up): asked of Jev alongside the Topic's own
 * tags and stored only as raw probabilities in `social_items.tag_scores`.
 * Never in user API responses, the UI or digests until a tag graduates to
 * `shown`. Pack files live in `apps/backend/src/social-listening/tag-registry/packs`.
 */
export const SOCIAL_TAG_TYPES = ["noul", "choice", "multi"] as const;
/** `noul`: one probability; `choice`: one of `options`; `multi`: one noul per option. */
export type SocialTagType = (typeof SOCIAL_TAG_TYPES)[number];

/** What a tag is about. Never a person: no tag may infer anything about someone. */
export const SOCIAL_TAG_SUBJECTS = ["content", "account", "product"] as const;
export type SocialTagSubject = (typeof SOCIAL_TAG_SUBJECTS)[number];

/** `post`: once per post; `perBrand`: once per Topic brand. */
export const SOCIAL_TAG_SCOPES = ["post", "perBrand"] as const;
export type SocialTagScope = (typeof SOCIAL_TAG_SCOPES)[number];

export const SOCIAL_TAG_STATUSES = ["silent", "shown"] as const;
export type SocialTagStatus = (typeof SOCIAL_TAG_STATUSES)[number];

/** When a tag is asked; every condition set must hold. */
export interface SocialTagAppliesWhen {
  /** Skipped for a post already known not relevant. */
  relevant?: boolean;
  /** Per-brand tags: skipped for a brand already known absent. */
  brandPresent?: boolean;
  /** Asked only when the post has stored images. */
  hasImage?: boolean;
}

/** One tag of a pack file. */
export interface SocialTagRegistryEntry {
  /** snake_case, unique in the pack. */
  key: string;
  type: SocialTagType;
  /** The Jev instructions; reads "this post". */
  prompt: string;
  description?: string;
  /** Option key (snake_case) -> criterion; required for choice and multi. */
  options?: Record<string, string>;
  appliesWhen?: SocialTagAppliesWhen;
  scope: SocialTagScope;
  subject: SocialTagSubject;
  /** Definition version: bumped on any change, so the storage key moves and posts are re-asked. */
  since: number;
  status: SocialTagStatus;
  /** Read-time thresholds once shown; absent while silent. */
  thresholds?: Partial<Record<SocialPlatform, number>>;
}

/** Which Topics a pack is asked for. */
export interface SocialTagPackSelector {
  /** Every Topic (the core pack). */
  always?: boolean;
  /** Lowercase terms; one found in the Topic's industry node path selects the pack. */
  industryPathIncludes?: Array<string>;
}

/** Taxonomy subtrees a vertical pack opens. */
export interface SocialTagPackTaxonomy {
  /** IAB Content 3.1 `full_path`s whose subtree is asked as nouls. */
  contentPaths?: Array<string>;
  /** Terms searched in the IAB Audience 1.1 interest and purchase-intent branches. */
  audienceTerms?: Array<string>;
}

/** One pack file (`<pack>.v<version>.json`). */
export interface SocialTagPack {
  pack: string;
  version: number;
  description: string;
  selectWhen: SocialTagPackSelector;
  taxonomy?: SocialTagPackTaxonomy;
  tags: Array<SocialTagRegistryEntry>;
}

/** How the Topic's industry node was set. */
export const SOCIAL_INDUSTRY_SOURCES = ["inferred", "admin"] as const;
export type SocialIndustrySource = (typeof SOCIAL_INDUSTRY_SOURCES)[number];

/**
 * Back office only:
 *   PATCH /admin/organizations/:orgToken/social-listening/universes/:token/industry
 *         UpdateSocialUniverseIndustryRequest -> SocialUniverseIndustry
 * `industryNodeToken` null clears the node and lets the next edit infer it again.
 */
export interface UpdateSocialUniverseIndustryRequest {
  industryNodeToken: string | null;
}

export interface SocialUniverseIndustry {
  universeToken: string;
  industryNodeToken: string | null;
  industrySource: SocialIndustrySource | null;
}

/* ------------------- Collection v2: typed sources and rounds ------------- */
/*
 * BUILD-PLAN v6 (docs/social-listening/collection-fixes/BUILD-PLAN.md). The
 * contracts below are the one declaration every later lane builds on: typed
 * sources per channel, the plan derivation (D15, D16, D23), the end table and
 * the API shapes. Added unread: the live collect still runs the old path
 * until the job switches (L6).
 */

/** What a source is checked as: a literal provider read, never a reading of what the words mean. */
export const SOCIAL_SOURCE_CHECK_STATUSES = [
  "unchecked",
  "verified",
  "not_found",
  "inactive",
  "age_restricted",
] as const;
export type SocialSourceCheckStatus =
  (typeof SOCIAL_SOURCE_CHECK_STATUSES)[number];

/**
 * The check of a hashtag, community or account (L9): `verified` when the
 * provider read holds a post in the window (an account: its posts feed; a
 * community: weekly active above 0); `not_found` when the provider has no
 * such source (an account: HTTP 404); `inactive` when it exists with nothing
 * in the window (an account exists when the provider's page names it, by its
 * channel or user id: a page that names none proves nothing);
 * `age_restricted` when the provider refuses an account's posts behind the
 * platform's age gate (HTTP 403 saying so, no credit): it exists but its
 * posts cannot be read, so it never collects; `unchecked`
 * before a check ran, or when it gave no verdict (the click's check credits
 * ran out, or the `failure` of its result says why).
 */
export interface SocialSourceCheck {
  status: SocialSourceCheckStatus;
  /** ISO time of the check; null while unchecked. */
  checkedAt: string | null;
  /** Newest post the check saw; null when it saw none or did not run. */
  lastPostAt: string | null;
  /** Posts the check saw inside the window; null when it did not run. */
  postsInWindow: number | null;
  /** Communities only: the provider's weekly active count; null elsewhere. */
  weeklyActive: number | null;
}

/** The check of a source no check has read yet. */
export const SOCIAL_SOURCE_UNCHECKED: Readonly<SocialSourceCheck> = {
  status: "unchecked",
  checkedAt: null,
  lastPostAt: null,
  postsInWindow: null,
  weeklyActive: null,
};

/** One search of a channel. The id is stable and the query is data: rewording a search never makes a new source. */
export interface SocialChannelSearch {
  id: string;
  query: string;
}

/** One hashtag a channel follows, written without the `#`. */
export interface SocialChannelHashtag {
  id: string;
  tag: string;
  check: SocialSourceCheck;
}

/**
 * How a community (Reddit's subreddit) is read: `narrow` limits the channel
 * to it (user-typed, and Reddit handles saved before v2); `add` searches it
 * beside the whole-platform search (Suggest-verified).
 */
export const SOCIAL_COMMUNITY_MODES = ["narrow", "add"] as const;
export type SocialCommunityMode = (typeof SOCIAL_COMMUNITY_MODES)[number];

/** One community of a channel, by its name as written (the adapter strips an `r/` prefix, as today). */
export interface SocialChannelCommunity {
  id: string;
  name: string;
  mode: SocialCommunityMode;
  check: SocialSourceCheck;
}

/**
 * The one identity of a channel input (a search, a hashtag or a community):
 * its words with spacing and case folded. The providers match searches and
 * name hashtags and subreddits case-insensitively, so "MexicoCity" and
 * "mexicocity" are one community and "AT&T  méxico" and "AT&T México" one
 * search (collection reads them as one, `deriveChannelSources`). Every list
 * of a channel's inputs keeps one entry per key, with the first spelling it
 * got. Exact after folding: never a fuzzy or partial match.
 */
export function socialInputKey(value: string): string {
  return value.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
}

/** A channel's typed inputs, as stored on its tracker (`searches`, `hashtags`, `communities`). */
export interface SocialTypedChannelInputs {
  searches: Array<SocialChannelSearch>;
  hashtags: Array<SocialChannelHashtag>;
  communities: Array<SocialChannelCommunity>;
}

/**
 * The one writer of a channel's typed inputs while the wire still sends
 * `searches: string[]` and `handles` (until L9 switches the wire): the same
 * literal transform as migration 0247. On TikTok and Instagram an entry whose
 * first character is `#` is a hashtag, stored without it; every other entry
 * is a search, unchanged. On Reddit each handle is a `narrow` community;
 * handles on the other platforms have no typed home and stay in `handles`.
 * A handle whose `socialInputKey` an earlier one has is the same
 * community and is left out. An entry whose value equals one in `current` (exactly, in that list) keeps
 * that entry whole: its id, and for a hashtag or community its check and
 * mode. Every other entry gets `newId()` and an unchecked check. Order
 * follows the input.
 */
export function socialTypedChannelInputs(
  platform: SocialPlatform,
  keywords: ReadonlyArray<string>,
  handles: ReadonlyArray<string>,
  current: SocialTypedChannelInputs | null,
  newId: () => string,
): SocialTypedChannelInputs {
  const take = <T>(
    entries: ReadonlyArray<T>,
    valueOf: (entry: T) => string,
  ) => {
    const left = [...entries];
    return (value: string): T | null => {
      const index = left.findIndex((entry) => valueOf(entry) === value);
      return index < 0 ? null : left.splice(index, 1)[0];
    };
  };
  const reuseSearch = take(current?.searches ?? [], (entry) => entry.query);
  const reuseHashtag = take(current?.hashtags ?? [], (entry) => entry.tag);
  const reuseCommunity = take(
    current?.communities ?? [],
    (entry) => entry.name,
  );
  const followsHashtags = platform === "tiktok" || platform === "instagram";
  const result: SocialTypedChannelInputs = {
    searches: [],
    hashtags: [],
    communities: [],
  };
  for (const value of keywords) {
    if (followsHashtags && value.startsWith("#")) {
      const tag = value.slice(1);
      result.hashtags.push(
        reuseHashtag(tag) ?? {
          id: newId(),
          tag,
          check: { ...SOCIAL_SOURCE_UNCHECKED },
        },
      );
    } else {
      result.searches.push(reuseSearch(value) ?? { id: newId(), query: value });
    }
  }
  if (platform === "reddit") {
    for (const name of handles) {
      if (
        result.communities.some(
          (community) =>
            socialInputKey(community.name) === socialInputKey(name),
        )
      ) {
        continue;
      }
      result.communities.push(
        reuseCommunity(name) ?? {
          id: newId(),
          name,
          mode: "narrow",
          check: { ...SOCIAL_SOURCE_UNCHECKED },
        },
      );
    }
  }
  return result;
}

/**
 * The old columns (`keywords`, `handles`) a channel's typed inputs mirror for
 * one release (P9), so the old collect and an old image read the same
 * sources: each search's query, then on TikTok and Instagram each hashtag as
 * `#tag` (the old form of a hashtag); on Reddit each `narrow` community is a
 * handle. An `add` community has no old form: the old collect would read a
 * Reddit handle as narrowing the channel, so it is left out of the mirror and
 * read only by the typed collect. `accountHandles` (TikTok, Instagram,
 * YouTube) stay as they are. The inverse of `socialTypedChannelInputs`.
 */
export function socialLegacyChannelColumns(
  platform: SocialPlatform,
  typed: SocialTypedChannelInputs,
  accountHandles: ReadonlyArray<string>,
): { keywords: Array<string>; handles: Array<string> } {
  const followsHashtags = platform === "tiktok" || platform === "instagram";
  return {
    keywords: [
      ...typed.searches.map((search) => search.query),
      ...(followsHashtags
        ? typed.hashtags.map((hashtag) => `#${hashtag.tag}`)
        : []),
    ],
    handles:
      platform === "reddit"
        ? typed.communities
            .filter((community) => community.mode === "narrow")
            .map((community) => community.name)
        : [...accountHandles],
  };
}

/** What a source check reads: a hashtag feed, a community's details, or an account's posts feed. */
export const SOCIAL_SOURCE_CHECK_KINDS = [
  "hashtag",
  "community",
  "account",
] as const;
export type SocialSourceCheckKind = (typeof SOCIAL_SOURCE_CHECK_KINDS)[number];

/**
 * Which platforms also search the Topic's brands (`search.brands` in
 * `config/social-providers.yaml`): the client's copy, for the channel card's
 * brand line; a backend spec holds it equal to the file.
 */
export const SOCIAL_PLATFORM_SEARCHES_BRANDS: Readonly<Record<SocialPlatform, boolean>> = {
  reddit: true,
  youtube: true,
  tiktok: true,
  instagram: true,
};

/**
 * Which sources a platform takes, per check kind: a hashtag where its search
 * rules follow hashtags (`search.hashtags: feed`), a community where they take
 * communities (`search.communities`), a brand account where its provider
 * offers own posts (`capabilities.ownPosts`). The client's copy of those
 * `config/social-providers.yaml` facts; a backend spec holds it equal to the
 * file (`socialSourceCheckOffered`).
 */
export const SOCIAL_PLATFORM_SOURCE_KINDS: Readonly<
  Record<SocialPlatform, Readonly<Record<SocialSourceCheckKind, boolean>>>
> = {
  reddit: { hashtag: false, community: true, account: false },
  youtube: { hashtag: false, community: false, account: true },
  tiktok: { hashtag: true, community: false, account: true },
  instagram: { hashtag: true, community: false, account: true },
};

/** One source to check: a hashtag (without `#`), a community, or an account handle. */
export interface SocialSourceCheckTarget {
  platform: SocialPlatform;
  kind: SocialSourceCheckKind;
  value: string;
}

/** Most provider credits one click's source checks spend (L9). */
export const SOCIAL_SOURCE_CHECK_MAX_CREDITS = 50;

/**
 * How one click's source checks ended:
 * - `complete`: every source was checked (each check may still say `not_found` or `inactive`);
 * - `partial`: some sources stayed unchecked, for `partialReason`;
 * - `spend_cap`: the org's monthly cap was already spent, so no check ran.
 */
export const SOCIAL_SOURCE_CHECK_BATCH_STATUSES = [
  "complete",
  "partial",
  "spend_cap",
] as const;
export type SocialSourceCheckBatchStatus =
  (typeof SOCIAL_SOURCE_CHECK_BATCH_STATUSES)[number];

/**
 * Why a batch is `partial`: `credit_cap` the click's SOCIAL_SOURCE_CHECK_MAX_CREDITS
 * (or what is left of the org cap) was reached; `provider_out_of_credits` the
 * provider account is out of credits; `provider_not_configured` no provider
 * key; `provider_error` a check failed; `timeout` a check had no answer within
 * its time (`sourceChecks.timeoutMs`, config/social-providers.yaml);
 * `empty_feed` an account's posts feed came back empty and named no account:
 * YouTube answers a handle that does not exist and a real channel with nothing
 * in that feed (one that posts only Shorts) alike, so the check has no verdict
 * on it (the page was charged); `paused` the kill switch or the global
 * breaker stopped paid calls. The source stays unchecked.
 */
export const SOCIAL_SOURCE_CHECK_PARTIAL_REASONS = [
  "credit_cap",
  "provider_out_of_credits",
  "provider_not_configured",
  "provider_error",
  "paused",
  "timeout",
  "empty_feed",
] as const;
export type SocialSourceCheckPartialReason =
  (typeof SOCIAL_SOURCE_CHECK_PARTIAL_REASONS)[number];

/** Why one source of a batch stayed `unchecked`: the org cap was spent, or the batch's partial reason that hit it. */
export type SocialSourceCheckFailure =
  | "spend_cap"
  | SocialSourceCheckPartialReason;

/** One click's source checks, stated. */
export interface SocialSourceCheckSummary {
  status: SocialSourceCheckBatchStatus;
  /** Set exactly when `status` is `partial`. */
  partialReason: SocialSourceCheckPartialReason | null;
  /** Provider credits the checks spent; booked as one cost event. */
  credits: number;
  /** Sources a check read (verified, inactive or not found). */
  checked: number;
  /** Sources left `unchecked`. */
  unchecked: number;
}

/**
 * `POST /social-listening/universes/sources/check` (manage): checks sources the
 * client typed (a brand account), as one click under the same caps as
 * Suggest's checks. At most SOCIAL_SOURCE_CHECK_MAX_CREDITS targets.
 */
export interface SocialSourceCheckRequest {
  targets: Array<SocialSourceCheckTarget>;
  /** The Topic's range; each check reads the channel's first-import window from it. Default SOCIAL_DEFAULT_DEPTH_DAYS. */
  depthDays?: SocialDepthDays;
  marketCode?: string | null;
}

export interface SocialSourceCheckResponse {
  /**
   * One per target, in target order; `providerId` is an account's provider id
   * once found; `failure` says why the check stayed `unchecked`, null exactly
   * when it ran.
   */
  results: Array<{
    check: SocialSourceCheck;
    providerId: string | null;
    failure: SocialSourceCheckFailure | null;
  }>;
  summary: SocialSourceCheckSummary;
}

/** A source Suggest proposed that the form did not keep: its check found no such source. */
export interface SocialRefusedSource {
  platform: SocialPlatform;
  kind: SocialSourceCheckKind;
  /** The tag, community name or account handle, as proposed. */
  value: string;
  /** The brand an account was proposed for; null for hashtags and communities. */
  brandName: string | null;
  refusal: "not_found";
}

/**
 * What Suggest says about the Topic's brands: `proposed` brands are listed;
 * `category_only` the subject is a product category with no brand of its own
 * and the model named no brand (category-only Topics exist); `none_usable`
 * the model named brands but none had a usable name, role and search.
 */
export const SOCIAL_SUGGESTED_BRANDS_OUTCOMES = [
  "proposed",
  "category_only",
  "none_usable",
] as const;
export type SocialSuggestedBrandsOutcome =
  (typeof SOCIAL_SUGGESTED_BRANDS_OUTCOMES)[number];

/**
 * How a source collects. Routes, not strings: a value never decides its route.
 * `comment_search` (D23, Reddit) searches comments for one of the Topic's or
 * a brand's searches, newest first, and keeps those dated in the period, each
 * with its thread as a context post; it is a source of the comments plan
 * group, so its pages spend the comment credits, never a post group's budget.
 */
export const SOCIAL_COLLECT_ROUTES = [
  "search",
  "hashtag",
  "own_posts",
  "tagged_posts",
  "comments",
  "comment_search",
  "community_search",
  "community_feed",
] as const;
export type SocialCollectRoute = (typeof SOCIAL_COLLECT_ROUTES)[number];

/**
 * A plan group: the Topic's category sources, one brand's (`brand:<brandId>`),
 * or the run's comment sources (`comments`, D13). The comments group has its
 * own credit budget (`commentCredits`) and a target of 0: it never draws on a
 * post group's budget and never counts toward T.
 */
export type SocialPlanGroupKey = "topic" | `brand:${string}` | "comments";

/** The plan group of a run's comment sources (D13): budget in credits, target 0. */
export const SOCIAL_COMMENTS_PLAN_GROUP = "comments" as const;

/** The plan group of a brand's sources. */
export function socialBrandGroup(brandId: string): SocialPlanGroupKey {
  return `brand:${brandId}`;
}

/** One typed source of a channel's collect, derived by `deriveChannelSources` (L5) from typed fields only. */
export interface SocialCollectSource {
  /** Stable: the input's id, or derived from (brandId, platform, route); never parsed from the value. */
  id: string;
  route: SocialCollectRoute;
  /** Data only: the query, tag, provider id, community or parent post id. */
  value: string;
  group: SocialPlanGroupKey;
  brandId: string | null;
  /** `comments` only: the accepted parent post (`social_items.id`). */
  parentItemId: number | null;
}

/**
 * Why a successful run stopped collecting (`collect_stop`): the channel's
 * accepted posts reached its target with the topic guard held; a group
 * stopped on its fetch budget or the round limit was reached; or no source
 * was left live (an empty provider result included). Abnormal ends keep
 * their run status and failure reason instead (SOCIAL_RUN_ENDS).
 */
export const SOCIAL_RUN_STOP_REASONS = [
  "target_met",
  "budget_reached",
  "exhausted",
] as const;
export type SocialRunStopReason = (typeof SOCIAL_RUN_STOP_REASONS)[number];

/**
 * Why one source stopped:
 * - `no_new_ids`: a page brought no post not seen before (never on prior-run
 *   sightings of a continued source);
 * - `end`: the cursor ended, or the route answered 404;
 * - `max_pages`: the route's page guard (`config/social-providers.yaml`);
 * - `no_recent_posts`: `stalePages` pages in a row with no new in-window post;
 * - `past_window`: a newest-first feed (own posts, community feed, Reddit's
 *   comment search) reached items older than the window; never a post search;
 * - `blocked`, `error`: the provider refused or failed this source;
 * - `cursor_expired`: the provider refused a saved page cursor;
 * - `budget_share`: its plan group fetched its budget;
 * - `unproductive`: 2 pages or more with 0 accepted and 0 unknown;
 * - `max_rounds`: still live when the run reached SOCIAL_MAX_ROUNDS;
 * - `target_met`: the channel met its target;
 * - `transcript_budget`, `comment_budget`: the run's transcript or comment
 *   credits ran out;
 * - the cut stops below: still live when its run closed on an abnormal end
 *   (`SocialRunEnd.sourceCut`), so every source of a closed run is typed.
 */
export const SOCIAL_SOURCE_STOP_REASONS = [
  "no_new_ids",
  "end",
  "max_pages",
  "no_recent_posts",
  "past_window",
  "blocked",
  "error",
  "cursor_expired",
  "budget_share",
  "unproductive",
  "max_rounds",
  "target_met",
  "transcript_budget",
  "comment_budget",
  // Cut by the run's end (SOCIAL_RUN_ENDS `sourceCut`):
  "spend_cap",
  "rate_limited",
  "out_of_credits",
  "judge_unavailable",
  "paused",
  "run_failed",
] as const;
export type SocialSourceStopReason =
  (typeof SOCIAL_SOURCE_STOP_REASONS)[number];

/** The stops a source carries when its run's abnormal end cut it while live; its window was not read whole. */
export const SOCIAL_SOURCE_CUT_STOPS = [
  "spend_cap",
  "rate_limited",
  "out_of_credits",
  "judge_unavailable",
  "paused",
  "run_failed",
  "blocked",
] as const satisfies ReadonlyArray<SocialSourceStopReason>;
export type SocialSourceCutStop = (typeof SOCIAL_SOURCE_CUT_STOPS)[number];

/** Who published or paid for a post, as the judge answers it (D9). Unknown is null, never organic. */
export const SOCIAL_POST_SOURCES = [
  "organic",
  "brand_owned",
  "sponsored",
] as const;
export type SocialPostSource = (typeof SOCIAL_POST_SOURCES)[number];

/**
 * Whether a source label meets its precision and recall bar on a platform
 * (Q15: sponsored precision >= 0.85 and recall >= 0.70, brand_owned
 * precision >= 0.85). Where false, the pill and the facet option are hidden
 * on that platform and the filter says the label is not available there.
 * Set from the hand-labelled measurement (L3 without transcripts, L7 with).
 *
 * L3 second pass, 2026-10-29: the v4 source wording defines the labels by
 * who published and who is paid or earns; 342 accepted posts of the set-B
 * re-record (~/qa/social-replay/2026-10-29-l3b-v4), labels in
 * docs/social-listening/collection-fixes/source-labels-l3 (17 of the first
 * pass corrected by a blind second pass of the 78 disagreement posts):
 * - youtube (n 109): sponsored P 0.86 R 0.93; brand_owned P 0.81 R 0.93
 *   (fails: tradespeople and explainer channels read as businesses);
 * - tiktok (n 151): sponsored P 0.77 R 0.71 (fails: bare brand tags read as
 *   paid); brand_owned P 0.95;
 * - instagram (n 59): sponsored P 0.92 R 1.00; brand_owned P 1.00;
 * - reddit (11 judged of 23; 5 sponsored, 1 brand_owned): too few for a bar.
 * Organic has no bar (Q15 grades the two labels only).
 *
 * L7, 2026-10-29, with transcripts (docs/social-listening/collection-fixes/
 * source-labels-l7): the same 260 TikTok/YouTube posts, live control vs
 * `post.transcript`. Against these speech-blind labels the transcript
 * flags spoken affiliate pitches the labellers never heard (TikTok
 * sponsored P 0.86 -> 0.70, R 0.68 -> 0.82). After a blind relabel of the 37
 * disagreement posts that heard the speech (18 changed):
 * - youtube: sponsored P 0.86 R 0.83 -> P 0.91 R 1.00; brand_owned P 0.87 -> 0.90;
 * - tiktok: sponsored P 0.95 R 0.58 -> P 0.94 R 0.86; brand_owned P 0.95 -> 0.95.
 * Every label clears the bar with transcripts; without them TikTok
 * sponsored misses on recall. The values below stay as L3 set them with
 * transcripts off; which values hold with transcripts on is the lead's call.
 */
export const SOCIAL_POST_SOURCE_LABEL_AVAILABLE: Readonly<
  Record<SocialPlatform, Readonly<Record<SocialPostSource, boolean>>>
> = {
  reddit: { organic: true, brand_owned: false, sponsored: false },
  youtube: { organic: true, brand_owned: false, sponsored: true },
  tiktok: { organic: true, brand_owned: true, sponsored: false },
  instagram: { organic: true, brand_owned: true, sponsored: true },
};

/* -------------------------------- Run ends ------------------------------- */

/** Every way a channel run ends; each maps to exactly one row of SOCIAL_RUN_ENDS. */
export const SOCIAL_RUN_END_KINDS = [
  "target_met",
  "budget_reached",
  "exhausted",
  "spend_cap",
  "rate_limited",
  "provider_out_of_credits",
  "judge_unavailable",
  "provider_blocked",
  "queue_lost",
  "replaced",
  "judge_not_configured",
  "provider_not_configured",
  "paused",
  "out_of_credits",
  "paused_by_ops",
  "skipped",
] as const;
export type SocialRunEndKind = (typeof SOCIAL_RUN_END_KINDS)[number];

/** How a run closes on one end. */
export interface SocialRunEnd {
  status: SocialSyncRunStatus;
  /** The status when the run found no post (the same as `status` except where a partial needs posts). */
  statusWithNoPosts: SocialSyncRunStatus;
  failureReason: SocialSyncRunFailureReason | null;
  /** The channel reason `socialChannelStatusReason` derives from a run closed with `status`, or with `statusWithNoPosts`. */
  channelReason: SocialChannelStatusReason | null;
  /** Set on successful closes only. */
  collectStop: SocialRunStopReason | null;
  /**
   * The stop each source still live at the close carries. Null on the
   * successful ends only, where the settle stops every live source first.
   */
  sourceCut: SocialSourceCutStop | null;
}

/**
 * The end table (BUILD-PLAN §3.2): the one owner of how a run closes.
 * `collect_stop` exists only on successful closes; abnormal ends reuse
 * today's statuses and failure reasons. A pause is the client's action, never
 * a failure: it closes `partial`, typed `paused`, with or without posts (the
 * channel itself reads `paused`). `judge_unavailable` closes partial with
 * posts unsorted, which the channel reads as `not_sorted`. Wallet and ops
 * ends: `out_of_credits` and `skipped` close before any provider call
 * (status `skipped`, nothing charged); `paused_by_ops` closes a run the kill
 * switch stopped mid-way, `partial` when it accepted posts (charged, D3),
 * else `failed`.
 */
export const SOCIAL_RUN_ENDS: Readonly<Record<SocialRunEndKind, SocialRunEnd>> =
  {
    target_met: {
      status: "succeeded",
      statusWithNoPosts: "succeeded",
      failureReason: null,
      channelReason: null,
      collectStop: "target_met",
      sourceCut: null,
    },
    budget_reached: {
      status: "succeeded",
      statusWithNoPosts: "succeeded",
      failureReason: null,
      channelReason: null,
      collectStop: "budget_reached",
      sourceCut: null,
    },
    exhausted: {
      status: "succeeded",
      statusWithNoPosts: "succeeded",
      failureReason: null,
      channelReason: null,
      collectStop: "exhausted",
      sourceCut: null,
    },
    spend_cap: {
      status: "over_budget",
      statusWithNoPosts: "over_budget",
      failureReason: null,
      channelReason: null,
      collectStop: null,
      sourceCut: "spend_cap",
    },
    rate_limited: {
      status: "rate_limited",
      statusWithNoPosts: "rate_limited",
      failureReason: null,
      channelReason: null,
      collectStop: null,
      sourceCut: "rate_limited",
    },
    provider_out_of_credits: {
      status: "partial",
      statusWithNoPosts: "failed",
      failureReason: "stopped_early",
      channelReason: "stopped_early",
      collectStop: null,
      sourceCut: "out_of_credits",
    },
    judge_unavailable: {
      status: "partial",
      statusWithNoPosts: "partial",
      failureReason: null,
      channelReason: "not_sorted",
      collectStop: null,
      sourceCut: "judge_unavailable",
    },
    provider_blocked: {
      status: "failed",
      statusWithNoPosts: "failed",
      failureReason: "provider_blocked",
      channelReason: null,
      collectStop: null,
      sourceCut: "blocked",
    },
    queue_lost: {
      status: "failed",
      statusWithNoPosts: "failed",
      failureReason: "queue_lost",
      channelReason: null,
      collectStop: null,
      sourceCut: "run_failed",
    },
    replaced: {
      status: "failed",
      statusWithNoPosts: "failed",
      failureReason: "replaced",
      channelReason: null,
      collectStop: null,
      sourceCut: "run_failed",
    },
    judge_not_configured: {
      status: "failed",
      statusWithNoPosts: "failed",
      failureReason: "judge_not_configured",
      channelReason: "judge_not_configured",
      collectStop: null,
      sourceCut: "run_failed",
    },
    provider_not_configured: {
      status: "failed",
      statusWithNoPosts: "failed",
      failureReason: "provider_not_configured",
      channelReason: "provider_not_configured",
      collectStop: null,
      sourceCut: "run_failed",
    },
    paused: {
      status: "partial",
      statusWithNoPosts: "partial",
      failureReason: "paused",
      channelReason: null,
      collectStop: null,
      sourceCut: "paused",
    },
    // The wallet could not hold the run's credits: closed before any provider call, nothing charged.
    out_of_credits: {
      status: "skipped",
      statusWithNoPosts: "skipped",
      failureReason: "out_of_credits",
      channelReason: null,
      collectStop: null,
      sourceCut: "out_of_credits",
    },
    // The kill switch or the global breaker stopped a run that reached a provider: posts it accepted are kept and charged (D3).
    paused_by_ops: {
      status: "partial",
      statusWithNoPosts: "failed",
      failureReason: "paused_by_ops",
      channelReason: null,
      collectStop: null,
      sourceCut: "paused",
    },
    // The kill switch or the global breaker stopped a run before any provider call: nothing charged.
    skipped: {
      status: "skipped",
      statusWithNoPosts: "skipped",
      failureReason: "paused_by_ops",
      channelReason: null,
      collectStop: null,
      sourceCut: "paused",
    },
  };

/** The run fields one end writes: status, failure reason and `collect_stop`. */
export function socialRunEndOutcome(
  end: SocialRunEndKind,
  postsFound: number,
): {
  status: SocialSyncRunStatus;
  failureReason: SocialSyncRunFailureReason | null;
  collectStop: SocialRunStopReason | null;
} {
  const row = SOCIAL_RUN_ENDS[end];
  return {
    status: postsFound > 0 ? row.status : row.statusWithNoPosts,
    failureReason: row.failureReason,
    collectStop: row.collectStop,
  };
}

/* ------------------------ Plan derivation (D15, D16, D23) ---------------- */

/** A plan group's fetch budget is this many times its target (in-window posts fetched). */
export const SOCIAL_FETCH_BUDGET_MULTIPLE = 10;
/** The update target is per this many days read (a weekly update reads it exactly). */
export const SOCIAL_TARGET_BASE_DAYS = 7;
/** No run with a fresh source aims below this many accepted posts. */
export const SOCIAL_MIN_RUN_TARGET = 10;
/** k: a round fetches at most k x (target - accepted) in-window posts, and at least one page per live group. */
export const SOCIAL_ROUND_FETCH_MULTIPLE = 2;
/** A run stops after this many rounds (`budget_reached`, its live sources `max_rounds`). */
export const SOCIAL_MAX_ROUNDS = 8;
/**
 * What a Topic is about (D24): a brand (its own or one it tracks) or a
 * category or theme. Suggest proposes it, the user sets it on step 1; Topics
 * saved before it are `category`.
 */
export const SOCIAL_SUBJECT_KINDS = ["brand", "category"] as const;
export type SocialSubjectKind = (typeof SOCIAL_SUBJECT_KINDS)[number];

/**
 * The topic group's share of the plan, by the Topic's subject kind (D24): on
 * a brand Topic the brand-free searches find few relevant posts, so most of
 * the effort goes to the brand groups. 1 when the channel has no brand group.
 */
export const SOCIAL_TOPIC_BUDGET_SHARE: Readonly<Record<SocialSubjectKind, number>> = {
  brand: 0.2,
  category: 0.5,
};
/** D16: each brand group's share is at least this, as a share of the update target. */
export const SOCIAL_MIN_BRAND_SHARE = 0.2;
/** Most relevant posts one channel run aims for (first collection or update), owner Q1. */
export const SOCIAL_FIRST_COLLECTION_MAX_POSTS = 1500;
/** Highest local back-office post limit per sync: the run ceiling `socialChannelRunPlan` sizes against. */
export const SOCIAL_TEST_POST_LIMIT_MAX = SOCIAL_FIRST_COLLECTION_MAX_POSTS;
/** Paid comment pages per parent post, after the free embedded page. */
export const SOCIAL_COMMENT_PAGES_MAX = 3;
/** Comment credits per accepted post of the target. */
export const SOCIAL_COMMENT_CREDITS_PER_TARGET = 1;
/** Transcript credits per accepted post of the target (owner Q3: at most 2 x T calls a run). */
export const SOCIAL_TRANSCRIPT_CREDITS_PER_TARGET = 2;
/** Posts the first media link of a round stores, so the first verdicts come early; later links store SOCIAL_MEDIA_BATCH_SIZE. */
export const SOCIAL_FIRST_MEDIA_BATCH_SIZE = 5;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Days a source reads: from its window start (an update's cursor, a first
 * import's period start) to the collect start. The one-hour overlap an
 * update's window adds before the cursor is never counted.
 */
export function socialReadDays(since: Date, collectStart: Date): number {
  return (collectStart.getTime() - since.getTime()) / DAY_MS;
}

/** A Topic's period as dates: `from` and `to` inclusive. */
export interface SocialTopicPeriod {
  from: Date;
  to: Date;
}

/**
 * The Topic's period (owner D23): one window for every channel. It starts
 * where the Topic's first import starts, the Topic's creation minus its
 * chosen look-back (`depthDays`, 7/30/90), and ends now. Both are stored
 * typed facts of the Topic row: the first import of every channel is
 * enqueued in the same transaction that creates the Topic (QA: every
 * Topic's first run starts within 20 ms of `created_at`), so the creation
 * is the first import's start without reading runs. Editing the look-back
 * moves the start with it, like the look-back the Topic shows.
 *
 * Its only owner. Every reporting read (KPIs, series, mood, counts,
 * authors, the header and the digests) reads activity dated inside it, and
 * collection follows it: every channel's first import reads from its start
 * (`socialRunPlan`), and the dialog estimate sizes a new source from it.
 */
export function socialTopicPeriod(
  universe: { createdAt: Date; depthDays: number },
  now: Date,
): SocialTopicPeriod {
  const from = new Date(universe.createdAt.getTime() - universe.depthDays * DAY_MS);
  return { from, to: new Date(Math.max(now.getTime(), from.getTime())) };
}

/** Accepted posts worth aiming for over `readDays`, before shares and caps. */
export function socialRawTarget(
  updateTarget: number,
  readDays: number,
): number {
  return (updateTarget * readDays) / SOCIAL_TARGET_BASE_DAYS;
}

/** In-window posts a group may fetch for its target. */
export function socialFetchBudget(target: number): number {
  return SOCIAL_FETCH_BUDGET_MULTIPLE * target;
}

/**
 * What a plan group reads: `first_import` (no source state: the Topic's period),
 * `update` (since the source's cursor), or `first_import_continued` (a first
 * import cut short, resumed with its own remaining target and budget).
 */
export const SOCIAL_PLAN_KINDS = [
  "first_import",
  "update",
  "first_import_continued",
] as const;
export type SocialPlanKind = (typeof SOCIAL_PLAN_KINDS)[number];

/** One fresh source of the plan (not a continued first import). */
export interface SocialPlanSourceInput {
  sourceId: string;
  group: SocialPlanGroupKey;
  kind: Exclude<SocialPlanKind, "first_import_continued">;
  /** Days the source reads, overlap excluded; greater than 0. */
  readDays: number;
}

/** A group's cut first import: its remaining target and budget, never re-derived. */
export interface SocialPlanResumeInput {
  group: SocialPlanGroupKey;
  targetLeft: number;
  budgetLeft: number;
  /** The sources sharing this remainder: every one the cut left unfinished, with a saved page cursor or none yet. */
  sourceIds?: Array<string>;
}

export interface SocialChannelRunPlanInput {
  /** The Topic's subject kind: picks the topic group's share (SOCIAL_TOPIC_BUDGET_SHARE, D24). */
  subjectKind: SocialSubjectKind;
  /** The channel's update target (`runPostLimit`, owner D12). */
  updateTarget: number;
  /** The back-office test limit (local only); null elsewhere. */
  testLimit: number | null;
  /**
   * The channel's first collection posts limit (`firstCollectionPostLimit`):
   * the target of its `first_import` groups, in place of the size derived
   * from `updateTarget`. Absent, first imports keep the derived size (a plan
   * of updates alone reads neither).
   */
  firstCollectionLimit?: number;
  sources: Array<SocialPlanSourceInput>;
  resumes: Array<SocialPlanResumeInput>;
}

/** One plan group (group, kind) of a run. */
export interface SocialPlanGroup {
  group: SocialPlanGroupKey;
  kind: SocialPlanKind;
  /** The widest interval its sources read, for display and coverage; null for a continued group. */
  readDays: number | null;
  /** Its share of the run target: sizes its budget, never an accepted quota. */
  target: number;
  /** In-window posts it may fetch. */
  budget: number;
  /** Its sources, in input order; a continued group's are those of its resume input. */
  sourceIds: Array<string>;
  /** True when it has nothing to do: a target or budget of 0; a continued group on a target of 0 alone. */
  closed: boolean;
}

/** One run's plan for one channel: the one derivation the backend and the estimate both read. */
export interface SocialChannelRunPlan {
  /** T, the run's accepted-posts target: the sum of the group targets, never stored. */
  target: number;
  roundFetchMultiple: number;
  maxRounds: number;
  /** The topic groups' own share of T: `target_met` needs this share accepted in topic groups, or them exhausted. */
  topicGuardShare: number;
  /** Fresh groups in the order their first source appears, then continued groups in resume order. */
  groups: Array<SocialPlanGroup>;
  commentCredits: number;
  transcriptCredits: number;
}

function assertPositiveInteger(value: number, name: string): void {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer, got ${value}`);
  }
}

function assertNonNegativeInteger(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(
      `${name} must be a non-negative integer, got ${value}`,
    );
  }
}

/** Splits `total` in proportion to `weights` with largest-remainder rounding; ties go to the earlier weight. */
function largestRemainderSplit(
  total: number,
  weights: Array<number>,
): Array<number> {
  const sum = weights.reduce((acc, weight) => acc + weight, 0);
  const quotas = weights.map((weight) => (total * weight) / sum);
  const parts = quotas.map((quota) => Math.floor(quota));
  let left = total - parts.reduce((acc, part) => acc + part, 0);
  const order = quotas
    .map((quota, index) => ({ index, remainder: quota - parts[index] }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of order) {
    if (left <= 0) {
      break;
    }
    parts[index] += 1;
    left -= 1;
  }
  return parts;
}

/**
 * The plan of one channel run (BUILD-PLAN §3.3, D15 + D16 + D23).
 *
 *   share_g = topic: SOCIAL_TOPIC_BUDGET_SHARE[subjectKind] (1 with no brand group);
 *             brand: max((1 - topic share) / nBrands, SOCIAL_MIN_BRAND_SHARE)
 *   w_(g,k) = share_g / n_g x sum over the fresh sources s of (g,k) of raw(readDays_s)
 *             (n_g: the fresh sources of g, every kind; with firstCollectionLimit,
 *             those of the source's own kind)
 *   T_first = the first_import groups: 0 with none, else
 *             min(testLimit, SOCIAL_FIRST_COLLECTION_MAX_POSTS, firstCollectionLimit)
 *             (no firstCollectionLimit: the T_update formula over them)
 *   T_update = the update groups: 0 with none, else
 *             min(testLimit, SOCIAL_FIRST_COLLECTION_MAX_POSTS, max(SOCIAL_MIN_RUN_TARGET,
 *             min(round(sum w), round(raw(longest readDays of its groups)))))
 *   target  = fresh: T_first (or T_update) x w / sum w over its kind's groups
 *             (largest remainder, summing to T_first or T_update);
 *             continued: targetLeft, never re-derived
 *   budget  = fresh: socialFetchBudget(target); continued: budgetLeft
 *   fresh total = min(ceiling, sum of the fresh targets): a mixed run over
 *             min(testLimit, SOCIAL_FIRST_COLLECTION_MAX_POSTS) scales every
 *             fresh target down by largest remainder
 *   T       = sum of the targets
 *
 * nBrands counts every brand group in the input, fresh or continued. A group
 * target sizes the group's budget and its part of T; it is not an accepted
 * quota. Throws a RangeError on an input that breaks an invariant (a
 * non-positive target or test limit, a read of 0 days or less, a repeated
 * source id or resumed group).
 */
export function socialChannelRunPlan(
  input: SocialChannelRunPlanInput,
): SocialChannelRunPlan {
  assertPositiveInteger(input.updateTarget, "updateTarget");
  if (input.testLimit !== null) {
    assertPositiveInteger(input.testLimit, "testLimit");
  }
  const sourceIds = new Set<string>();
  for (const source of input.sources) {
    if (sourceIds.has(source.sourceId)) {
      throw new RangeError(`source ${source.sourceId} is listed twice`);
    }
    sourceIds.add(source.sourceId);
    if (!Number.isFinite(source.readDays) || source.readDays <= 0) {
      throw new RangeError(
        `source ${source.sourceId} must read more than 0 days, got ${source.readDays}`,
      );
    }
  }
  const resumedGroups = new Set<SocialPlanGroupKey>();
  for (const resume of input.resumes) {
    if (resumedGroups.has(resume.group)) {
      throw new RangeError(`group ${resume.group} is resumed twice`);
    }
    resumedGroups.add(resume.group);
    assertNonNegativeInteger(resume.targetLeft, `${resume.group} targetLeft`);
    assertNonNegativeInteger(resume.budgetLeft, `${resume.group} budgetLeft`);
  }

  const brandGroups = new Set<SocialPlanGroupKey>();
  for (const { group } of [...input.sources, ...input.resumes]) {
    if (group !== "topic") {
      brandGroups.add(group);
    }
  }
  const topicShare =
    brandGroups.size === 0 ? 1 : SOCIAL_TOPIC_BUDGET_SHARE[input.subjectKind];
  const brandShare =
    brandGroups.size === 0
      ? 0
      : Math.max((1 - topicShare) / brandGroups.size, SOCIAL_MIN_BRAND_SHARE);

  // With a first collection limit, first imports and updates are sized
  // apart, so n_g counts a group's fresh sources of the same kind only: a new
  // search never dilutes the updates of the searches beside it.
  const sizedApart = input.firstCollectionLimit !== undefined;
  const perGroupKey = (source: SocialPlanSourceInput): string =>
    sizedApart ? `${source.group}|${source.kind}` : source.group;
  const freshPerGroup = new Map<string, number>();
  for (const source of input.sources) {
    const key = perGroupKey(source);
    freshPerGroup.set(key, (freshPerGroup.get(key) ?? 0) + 1);
  }

  interface FreshGroup {
    group: SocialPlanGroupKey;
    kind: SocialPlanSourceInput["kind"];
    readDays: number;
    weight: number;
    sourceIds: Array<string>;
  }
  const fresh: Array<FreshGroup> = [];
  for (const source of input.sources) {
    const share = source.group === "topic" ? topicShare : brandShare;
    const weight =
      (share / (freshPerGroup.get(perGroupKey(source)) ?? 1)) *
      socialRawTarget(input.updateTarget, source.readDays);
    const existing = fresh.find(
      (entry) => entry.group === source.group && entry.kind === source.kind,
    );
    if (existing) {
      existing.weight += weight;
      existing.readDays = Math.max(existing.readDays, source.readDays);
      existing.sourceIds.push(source.sourceId);
    } else {
      fresh.push({
        group: source.group,
        kind: source.kind,
        readDays: source.readDays,
        weight,
        sourceIds: [source.sourceId],
      });
    }
  }

  if (input.firstCollectionLimit !== undefined) {
    assertPositiveInteger(input.firstCollectionLimit, "firstCollectionLimit");
  }
  const ceiling = Math.min(
    input.testLimit ?? Number.POSITIVE_INFINITY,
    SOCIAL_FIRST_COLLECTION_MAX_POSTS,
  );
  // The brand floors can push the shares past 1 (many brands): the update
  // limit over the longest read still caps the sum, so a run never keeps
  // more than its limit and every group scales down by its weight.
  const derivedTarget = (entries: Array<FreshGroup>): number =>
    Math.min(
      ceiling,
      Math.max(
        SOCIAL_MIN_RUN_TARGET,
        Math.min(
          Math.round(entries.reduce((acc, entry) => acc + entry.weight, 0)),
          Math.round(
            socialRawTarget(
              input.updateTarget,
              Math.max(...entries.map((entry) => entry.readDays)),
            ),
          ),
        ),
      ),
    );
  // With a first collection limit, first imports aim at it and updates at
  // the weekly sizing; a mixed run sums both (owner, 2026-11-01). Without
  // one, every fresh group shares the one derived target.
  const freshTargets = new Array<number>(fresh.length).fill(0);
  const firstCollectionLimit = input.firstCollectionLimit;
  const parts: Array<{
    kinds: Array<SocialPlanSourceInput["kind"]>;
    limit: number | null;
  }> =
    firstCollectionLimit === undefined
      ? [{ kinds: ["first_import", "update"], limit: null }]
      : [
          { kinds: ["first_import"], limit: firstCollectionLimit },
          { kinds: ["update"], limit: null },
        ];
  for (const part of parts) {
    const indexes = fresh
      .map((entry, index) => (part.kinds.includes(entry.kind) ? index : -1))
      .filter((index) => index >= 0);
    if (indexes.length === 0) {
      continue;
    }
    const entries = indexes.map((index) => fresh[index]);
    const kindTarget =
      part.limit !== null
        ? Math.min(ceiling, part.limit)
        : derivedTarget(entries);
    const split = largestRemainderSplit(
      kindTarget,
      entries.map((entry) => entry.weight),
    );
    indexes.forEach((index, position) => {
      freshTargets[index] = split[position];
    });
  }
  // The test limit and the 1,500 ceiling cap the fresh total, not each part:
  // a mixed run scales both parts down to the ceiling, by their targets.
  const freshTotal = freshTargets.reduce((acc, value) => acc + value, 0);
  if (freshTotal > ceiling) {
    largestRemainderSplit(ceiling, freshTargets).forEach((value, index) => {
      freshTargets[index] = value;
    });
  }

  const groups: Array<SocialPlanGroup> = [
    ...fresh.map((entry, index) => {
      const target = freshTargets[index];
      const budget = socialFetchBudget(target);
      return {
        group: entry.group,
        kind: entry.kind,
        readDays: entry.readDays,
        target,
        budget,
        sourceIds: entry.sourceIds,
        closed: target === 0 || budget === 0,
      };
    }),
    ...input.resumes.map((resume) => ({
      group: resume.group,
      kind: "first_import_continued" as const,
      readDays: null,
      target: resume.targetLeft,
      budget: resume.budgetLeft,
      sourceIds: resume.sourceIds ?? [],
      // Posts left and no budget stays open: a source with no page read still
      // owes its first page, or its look-back would never be read.
      closed: resume.targetLeft === 0,
    })),
  ];

  const target = groups.reduce((acc, group) => acc + group.target, 0);
  const topicTarget = groups
    .filter((group) => group.group === "topic")
    .reduce((acc, group) => acc + group.target, 0);
  return {
    target,
    roundFetchMultiple: SOCIAL_ROUND_FETCH_MULTIPLE,
    maxRounds: SOCIAL_MAX_ROUNDS,
    topicGuardShare: target === 0 ? 0 : topicTarget / target,
    groups,
    commentCredits: SOCIAL_COMMENT_CREDITS_PER_TARGET * target,
    transcriptCredits: SOCIAL_TRANSCRIPT_CREDITS_PER_TARGET * target,
  };
}

/* ------------------------------- API shapes ------------------------------ */

/** One plan group of a run with what it accepted. */
export interface SocialRunGroupItem {
  group: SocialPlanGroupKey;
  kind: SocialPlanKind;
  readDays: number | null;
  target: number;
  budget: number;
  accepted: number;
}

/** Accepted posts by their typed source; `unknown` where the judge gave none. */
export interface SocialPostSourceSplit {
  organic: number;
  brand_owned: number;
  sponsored: number;
  unknown: number;
}

/** Comments read for a run: the parents whose comments were read, and the comments kept. */
export interface SocialCommentsRead {
  parents: number;
  comments: number;
  /** The run's accepted parents whose comments were not read, by typed reason (none when every one was read). */
  notRead: Partial<Record<SocialCommentsNotRead, number>>;
}

/**
 * Why an accepted post's comments were not read (`social_items.comments_not_read`,
 * written with `comments_status = none`): its comment source's own stop
 * (`error` when the provider refused it, e.g. Reddit's 403s; `blocked`;
 * `comment_budget` when the run's comment credits left it no first page, a
 * parent never given a source included), or `run_closed` when the run closed
 * before its comments were read.
 */
export type SocialCommentsNotRead = SocialSourceStopReason | "run_closed";

/** A channel's latest closed run, its typed outcome only (the status copy is unchanged). */
export interface SocialChannelLastRun {
  runToken: string;
  status: SocialSyncRunStatus;
  failureReason: SocialSyncRunFailureReason | null;
  stopReason: SocialRunStopReason | null;
  groups: Array<SocialRunGroupItem>;
  sourceSplit: SocialPostSourceSplit;
  transcriptsSkipped: number;
  commentsRead: SocialCommentsRead;
  finishedAt: string | null;
}

/** An open run's progress: grows while the run is open. */
export interface SocialChannelProgress {
  runToken: string;
  round: number;
  accepted: number;
  /** In-window posts fetched so far. */
  fetched: number;
  target: number;
}

/**
 * Why a brand gets no route on a channel: no account, or one the client has
 * not confirmed (`no_confirmed_account`); a confirmed account whose check
 * found no post in range, found none, or gave no answer (`account_inactive`,
 * `account_not_found`, `account_unchecked`, its check status; an age-gated
 * one, `account_age_restricted`); the platform
 * does not offer the route (`not_offered_by_provider`).
 */
export const SOCIAL_COVERAGE_SKIP_REASONS = [
  "no_confirmed_account",
  "account_inactive",
  "account_not_found",
  "account_age_restricted",
  "account_unchecked",
  "not_offered_by_provider",
] as const;
export type SocialCoverageSkipReason =
  (typeof SOCIAL_COVERAGE_SKIP_REASONS)[number];

/** One brand's coverage on one channel (D14, A10): equal effort, or a stated reason why not. */
export interface SocialBrandCoverage {
  routes: Array<SocialCollectRoute>;
  skipped: Array<{
    route: SocialCollectRoute;
    reason: SocialCoverageSkipReason;
  }>;
  /** Accepted posts of the brand's groups in the channel's latest closed run. */
  accepted: number;
  /** Accepted per provider credit; null when the groups spent no credit. */
  acceptedPerCredit: number | null;
  /** The kind and days of the brand's widest group in that run; null when it had no group there (no source on the channel then). */
  kind: SocialPlanKind | null;
  readDays: number | null;
}

/** One kept comment, under its accepted parent post. Comments never count as posts. */
export interface SocialPostComment {
  token: string;
  platform: SocialPlatform;
  authorHandle: string | null;
  /** The comment's own date; null when the platform gave none (never the parent's date). */
  publishedAt: string | null;
  text: string;
  engagement: SocialPostEngagement;
  url: string | null;
}

/** `GET .../posts/:token/comments` (L11). */
export interface SocialPostCommentsResponse {
  comments: Array<SocialPostComment>;
  /** Null when the post's comments were not read. */
  commentsCollected: number | null;
  commentsReadAt: string | null;
  /** Why they were not read; null when they were. */
  commentsNotRead: SocialCommentsNotRead | null;
}
